import React, { useEffect, useMemo, useRef, useState } from "react";

/** ======= CONFIG ======= */
const BASE = "/data/nascar";
const DATA_FILE = "NASCAR Loop Data.json";

const ACTIVE_DATA_SOURCES = {
  cup: "/data/nascar/cup/latest/data.json",
  xfinity: "/data/nascar/xfinity/latest/data.json",
  trucks: "/data/nascar/trucks/latest/data.json",
};

/* ---------------- track groups ---------------- */
const TRACK_GROUPS = {
  superspeedways: ["Daytona International Speedway", "Atlanta Motor Speedway", "Talladega Superspeedway"],

  road_courses: [
    "Charlotte Motor Speedway Road Course",
    "Circuit of The Americas",
    "st pete",
    "Lime Rock Park",
    "San Diego Street Course",
    "san diego",
    "sebring",
    "Indianapolis Motor Speedway Road Course",
    "Sonoma Raceway",
    "Watkins Glen International",
    "Autódromo Hermanos Rodríguez",
    "Chicago Street Race",
    "Portland International Raceway",
  ],

  intermediates: [
    "Charlotte Motor Speedway",
    "Chicagoland Speedway",
    "Darlington Raceway",
    "Dover International Speedway",
    "Homestead-Miami Speedway",
    "Kansas Speedway",
    "Las Vegas Motor Speedway",
    "Michigan International Speedway",
    "Nashville Superspeedway",
    "Rockingham Speedway",
    "Texas Motor Speedway",
  ],
  concrete: ["bristol motor speedway", "nashville superspeedway", "dover international speedway"],

  short: [
    "bristol motor speedway",
    "dover international speedway",
    "iowa speedway",
    "Lucas Oil Indianapolis Raceway Park",
    "martinsville speedway",
    "new hampshire motor speedway",
    "north wilkesboro speedway",
    "phoenix raceway",
    "Rockingham Speedway",
    "richmond raceway",
    "World Wide Technology Raceway at Gateway",
    "World Wide Technology Raceway",
  ],

  short_flat: [
    "iowa speedway",
    "Lucas Oil Indianapolis Raceway Park",
    "martinsville speedway",
    "new hampshire motor speedway",
    "north wilkesboro speedway",
    "phoenix raceway",
    "richmond raceway",
    "World Wide Technology Raceway at Gateway",
    "World Wide Technology Raceway",
  ],

  large_oval: ["Indianapolis Motor Speedway", "Michigan International Speedway", "pocono raceway"],
};

/* ---------------- exhibition races (Cup only) ----------------
   Exhibition status is determined from RaceName instead of hard-coded dates.
   Any Cup race containing Clash, Duel, or All Star / All-Star is exhibition.
*/
function isCupSeriesName(seriesName) {
  const s = lower(seriesName);
  return s.includes("cup");
}

function isExhibitionRace(seriesName, raceName) {
  if (!isCupSeriesName(seriesName)) return false;

  const name = String(raceName ?? "")
    .toLowerCase()
    .replace(/[\u2010-\u2015-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return (
    name.includes("clash") ||
    name.includes("duel") ||
    name.includes("all star")
  );
}

/** ======= HELPERS ======= */
async function fetchJson(url) {
  const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status} @ ${url}`);
  const txt = await res.text();
  const ct = res.headers.get("content-type") || "";
  const looksJson = ct.includes("application/json") || /^[\[{]/.test(txt.trim());
  if (!looksJson) throw new Error(`Not JSON @ ${url}`);
  try {
    return JSON.parse(txt);
  } catch {
    throw new Error(`Invalid JSON @ ${url}`);
  }
}

function exportToCsv(filename, rows, columns) {
  const cleanExportValue = (col, raw) => {
    if (raw == null) return "";

    let cell = String(raw);
    const colKey = String(col).trim().toLowerCase();

    if (colKey === "driver_name" || colKey === "driver" || colKey === "name") {
      cell = cell.replace(/ Jr\./g, " Jr");
      cell = cell.replace(/ Jr\.$/g, " Jr");
    }

    if (colKey === "car") {
      cell = normalizeDisplayCarNumber(cell);

      // Preserve leading zeroes when the CSV is opened in Excel.
      // Example: car 07 stays displayed as 07 instead of becoming 7.
      if (cell !== "") {
        cell = `="${cell}"`;
      }
    }

    return cell;
  };

  const header = columns.join(",");
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const cell = cleanExportValue(col, row?.[col]);
          return '"' + cell.replaceAll('"', '""') + '"';
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Save the complete SVG viewBox, independent of the visible viewport.
// A small image-only PDF writer keeps this export dependency-free.
function chartImagePdf(jpegBytes, pixelWidth, pixelHeight, width, height) {
  const encode = (value) => new TextEncoder().encode(value);
  const parts = [];
  const offsets = [0];
  let length = 0;
  const append = (value) => {
    const bytes = typeof value === "string" ? encode(value) : value;
    parts.push(bytes);
    length += bytes.length;
  };
  const object = (id, body) => {
    offsets[id] = length;
    append(`${id} 0 obj\n${body}\nendobj\n`);
  };
  // PDF page dimensions are points. Keep even unusually tall charts valid.
  const factor = Math.min(0.75, 14000 / Math.max(width, height));
  const w = (width * factor).toFixed(3);
  const h = (height * factor).toFixed(3);
  append("%PDF-1.4\n");
  object(1, "<< /Type /Catalog /Pages 2 0 R >>");
  object(2, "<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
  object(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] /Resources << /XObject << /Chart 4 0 R >> >> /Contents 5 0 R >>`);
  offsets[4] = length;
  append(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${pixelWidth} /Height ${pixelHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`);
  append(jpegBytes);
  append("\nendstream\nendobj\n");
  const content = `q\n${w} 0 0 ${h} 0 0 cm\n/Chart Do\nQ\n`;
  object(5, `<< /Length ${encode(content).length} >>\nstream\n${content}endstream`);
  const xref = length;
  append("xref\n0 6\n0000000000 65535 f \n");
  for (let id = 1; id <= 5; id++) {
    append(`${String(offsets[id]).padStart(10, "0")} 00000 n \n`);
  }
  append(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return new Blob(parts, { type: "application/pdf" });
}

async function saveChartPdf(svg, filename, background) {
  if (!svg) throw new Error("Choose a chart with data before saving.");
  if (document.fonts?.ready) await document.fonts.ready;
  const { width, height } = svg.viewBox.baseVal;
  if (!(width > 0 && height > 0)) throw new Error("The chart has no dimensions.");
  const copy = svg.cloneNode(true);
  copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  copy.setAttribute("width", String(width));
  copy.setAttribute("height", String(height));
  copy.removeAttribute("class");
  copy.style.cssText = `font-family: ${getComputedStyle(svg).fontFamily}; background: ${background};`;

  // Standalone SVG images cannot load relative logo URLs; embed their bytes.
  const logoData = new Map();
  await Promise.all(Array.from(copy.querySelectorAll("image")).map(async (node) => {
    const href = node.getAttribute("href") || node.getAttributeNS("http://www.w3.org/1999/xlink", "href");
    if (!href || href.startsWith("data:")) return;
    if (!logoData.has(href)) {
      logoData.set(href, (async () => {
        const response = await fetch(new URL(href, document.baseURI).href, { signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error("A chart logo could not be loaded. Wait for the chart to finish loading and try again.");
        const blob = await response.blob();
        if (!blob.type.startsWith("image/")) throw new Error("A chart logo did not return an image.");
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error("A chart logo could not be read."));
          reader.readAsDataURL(blob);
        });
      })());
    }
    node.setAttribute("href", await logoData.get(href));
    node.removeAttributeNS("http://www.w3.org/1999/xlink", "href");
  }));

  const svgUrl = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(copy)], { type: "image/svg+xml;charset=utf-8" }));
  const canvas = document.createElement("canvas");
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      const timer = setTimeout(() => reject(new Error("Chart rendering timed out. Please try again.")), 15000);
      image.onload = () => { clearTimeout(timer); resolve(image); };
      image.onerror = () => { clearTimeout(timer); reject(new Error("The chart could not be rendered for PDF.")); };
      image.src = svgUrl;
    });
    // 2x resolution for sharp labels, capped for browser canvas limits.
    const scale = Math.min(2, 16000 / width, 16000 / height, Math.sqrt(24000000 / (width * height)));
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Your browser could not create the PDF image.");
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const jpeg = await new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("The chart image could not be created.")), "image/jpeg", 0.98));
    const pdf = chartImagePdf(new Uint8Array(await jpeg.arrayBuffer()), canvas.width, canvas.height, width, height);
    const pdfUrl = URL.createObjectURL(pdf);
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = filename;
    document.body.appendChild(link);
    try { link.click(); } finally {
      link.remove();
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    }
  } finally {
    URL.revokeObjectURL(svgUrl);
    canvas.width = 0;
    canvas.height = 0;
  }
}


function fmt(v) {
  if (v == null || v === "") return "";
  if (typeof v === "number") {
    const rounded = Math.round(v * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded);
  }
  return String(v);
}

function lower(s) {
  return String(s ?? "").toLowerCase();
}

function normalizeSeriesKey(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

function seriesBucket(value) {
  const s = normalizeSeriesKey(value);
  if (s.includes("xfinity") || s.includes("oreilly")) return "xfinity";
  if (s.includes("truck")) return "trucks";
  if (s.includes("cup")) return "cup";
  return "cup";
}

function publicSeriesLabel(value) {
  const s = String(value ?? "");
  return /xfinity/i.test(s) ? s.replace(/xfinity/gi, "O'Reilly") : s;
}

function formatEasternRaceDate(value) {
  if (value == null || value === "") return "";

  const s = String(value).trim();

  // NASCAR race dates in this file are stored as an Eastern wall-clock time
  // (example: 2026-08-14T19:30:00). Format that cleanly without letting the
  // browser shift the hour based on the viewer's local timezone.
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const hour24 = Number(m[4]);
    const minute = Number(m[5]);
    const ampm = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 || 12;
    return `${month}/${day}/${year} ${hour12}:${String(minute).padStart(2, "0")} ${ampm} ET`;
  }

  return s.replace("T", " ");
}

function normalizeDriverKey(name, { stripSuffix = false } = {}) {
  let s = String(name ?? "").toLowerCase().trim();
  s = s.replace(/[.,'’\-]/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  if (stripSuffix) {
    s = s.replace(/\s+(jr|sr|ii|iii|iv|v)\s*$/i, "").trim();
  }
  return s;
}

const DRIVER_NAME_ALIASES = new Map([
  ["john h nemechek", "john hunter nemechek"],
  ["john hunter nemechek", "john hunter nemechek"],
]);

function canonicalDriverKey(name, { stripSuffix = false } = {}) {
  const key = normalizeDriverKey(name, { stripSuffix });
  return DRIVER_NAME_ALIASES.get(key) || key;
}

function cleanDriverDisplayName(name) {
  let s = String(name ?? "").trim();
  if (!s) return "";

  // NASCAR sometimes appends/ prepends markers such as:
  // * Shane Van Gisbergen #
  // # Austin Hill(i)
  // Ryan Blaney (P)
  s = s.replace(/^[#*]+\s*/g, "");
  s = s.replace(/\s*[#*]+\s*$/g, "");
  s = s.replace(/\s*\((?:i|p)\)\s*$/gi, "");
  s = s.replace(/\s+(?:i|p)\s*$/gi, "");
  s = s.replace(/\s+/g, " ").trim();

  return s;
}

function normalizeDisplayCarNumber(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;

  // NASCAR source occasionally uses a 3-digit encoded car number.
  // Keep only the last two digits: 130 -> 30, 153 -> 53.
  return digits.length >= 3 ? digits.slice(-2) : digits;
}

/** case-insensitive column match */
function pickCol(allCols, candidates) {
  const map = new Map(allCols.map((c) => [lower(String(c).trim()), c]));
  for (const cand of candidates) {
    const hit = map.get(lower(String(cand).trim()));
    if (hit) return hit;
  }
  // fuzzy contains fallback
  for (const cand of candidates) {
    const c2 = allCols.find((c) => lower(String(c)).includes(lower(String(cand))));
    if (c2) return c2;
  }
  return "";
}

function safeYearFromDate(d) {
  if (!d) return "";
  const s = String(d);
  const y = s.slice(0, 4);
  return /^\d{4}$/.test(y) ? y : "";
}

function toNum(v) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** remove duplicate columns by case-insensitive+trim name */
function dedupeCols(cols) {
  const seen = new Set();
  const out = [];
  for (const c of cols) {
    const k = lower(String(c).trim());
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

/** ======= Pretty headers ======= */
function titleCaseWord(w) {
  if (!w) return "";
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

function prettyHeader(key) {
  const k = String(key ?? "").trim();
  if (!k) return "";

  const exact = {
    penn_rank: "Penn Rank",
    avg: "Avg Run",
    AvgRunningPos: "Avg Run",
    DriverRating: "Driver Rating",
    FastestLaps: "Fastest Laps",
    LapsLed: "Laps Led",
    LapsCompleted: "Laps Completed",
    GreenFlagPasses: "Green Flag Passes",
    GreenFlagPassed: "Green Flag Passed",
    PassDifferential: "Pass Differential",
    QualityPasses: "Quality Passes",
    Top15Laps: "Top 15 Laps",
    GFS: "GFS",
    GFSRank: "GFS Rank",
    GFLaps: "GF Laps",
    pct_laps_completed: "% Laps Completed",
    RaceDate: "Race Date",
    RaceName: "Race",
    CrewChief: "Crew Chief",
    MidRacePos: "Mid Race Pos",
    ClosingPos: "Closing Pos",
    ClosingLapsDiff: "Closing Laps Diff",
    BestPos: "Best Pos",
    WorstPos: "Worst Pos",
    dk_pts: "DK Pts",
    fd_pts: "FD Pts",
    races_count: "Races",
    entries_count: "Entries",
    date_start: "Start Date",
    date_end: "End Date",
    top_5: "Top 5",
    top_10: "Top 10",
    top_15: "Top 15",
    top_20: "Top 20",
  };
  if (exact[k]) return exact[k];

  // Split camelCase/PascalCase as well as underscores.
  const normalized = k
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/__+/g, "_");

  const ACR = new Set(["dk", "fd", "pct", "id", "gf", "gfs", "n", "pos"]);
  const parts = normalized.split("_").filter(Boolean);

  return parts
    .map((part) => {
      const lp = part.toLowerCase();
      if (lp === "dk") return "DK";
      if (lp === "fd") return "FD";
      if (lp === "pct") return "%";
      if (lp === "id") return "ID";
      if (lp === "pts") return "Pts";
      if (ACR.has(lp)) return lp.toUpperCase();
      if (/^\d+$/.test(lp)) return lp;
      return titleCaseWord(lp);
    })
    .join(" ")
    .replace(/\s+%/g, " %");
}

/** ======= Track type matching + aliases ======= */

function baseTrackKey(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// These only affect matching/filtering. The table still displays the track
// name exactly as it appears in NASCAR Loop Data.json.
const TRACK_ALIASES = new Map([
  ["gateway", "world wide technology raceway"],
  ["world wide technology raceway at gateway", "world wide technology raceway"],
  ["wwt raceway", "world wide technology raceway"],
  ["wwtr", "world wide technology raceway"],
  ["indianapolis motor speedway road course", "indianapolis motor speedway road course"],
  ["indy road course", "indianapolis motor speedway road course"],
  ["charlotte roval", "charlotte motor speedway road course"],
  ["charlotte motor speedway roval", "charlotte motor speedway road course"],
  ["cota", "circuit of the americas"],
  ["st pete", "st pete"],
  ["st petersburg", "st pete"],
  ["san diego", "san diego street course"],
  ["pocono", "pocono raceway"],
]);

function normTrackKey(s) {
  const key = baseTrackKey(s);
  return TRACK_ALIASES.get(key) || key;
}


/** Build lookup map ONCE using exact names only */
const TRACK_NAME_TO_TYPES = (() => {

  const map = new Map();

  for (const [typeKey, names] of Object.entries(TRACK_GROUPS)) {

    for (const name of names) {

      const key = normTrackKey(name);

      if (!key) continue;

      if (!map.has(key)) {
        map.set(key, new Set());
      }

      map.get(key).add(typeKey);

    }

  }

  return map;

})();



/** Exact lookup only */
function trackTypesForName(trackName) {

  const key = normTrackKey(trackName);

  if (!key) return [];

  const types = TRACK_NAME_TO_TYPES.get(key);

  return types ? Array.from(types) : [];

}



function matchesTrackTypes(trackName, selectedTypeKeys) {

  if (!selectedTypeKeys?.length) return true;

  const types = trackTypesForName(trackName);

  if (!types.length) return false;

  return selectedTypeKeys.some(t => types.includes(t));

}



function formatTrackTypes(typeKeys) {

  if (!typeKeys?.length) return "All";

  if (typeKeys.length === 1) return prettyHeader(typeKeys[0]);

  return "Multiple";

}

function numLogoSrc(carNum, seriesName) {
  const bucket = seriesBucket(seriesName);
  const folder =
    bucket === "xfinity" ? "Xfinity" :
    bucket === "trucks" ? "Trucks" :
    "cup";

  return `/logos/nascar/${folder}/${Math.round(carNum)}.png`;
}

/** ======= AUTO BAR COLORS FROM CAR LOGOS ======= */
const DOMINANT_LOGO_COLOR_CACHE = new Map();

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) return { h: 0, s: 0, l };

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;

  switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0);
      break;
    case g:
      h = (b - r) / d + 2;
      break;
    default:
      h = (r - g) / d + 4;
      break;
  }

  h /= 6;
  return { h, s, l };
}

function getDominantLogoColor(src, fallback = "#38bdf8") {
  if (!src || typeof window === "undefined") return Promise.resolve(fallback);
  if (DOMINANT_LOGO_COLOR_CACHE.has(src)) {
    return Promise.resolve(DOMINANT_LOGO_COLOR_CACHE.get(src));
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";

    img.onload = () => {
      try {
        const size = 48;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) throw new Error("Canvas unavailable");

        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        const pixels = ctx.getImageData(0, 0, size, size).data;
        const buckets = new Map();

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];
          if (a < 110) continue;

          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const lum = (r + g + b) / 3;

          // Ignore transparent/background-like white, black, and near-gray pixels.
          if (lum > 242 || lum < 24) continue;
          if (max - min < 18) continue;

          const qr = Math.round(r / 24) * 24;
          const qg = Math.round(g / 24) * 24;
          const qb = Math.round(b / 24) * 24;
          const key = `${qr},${qg},${qb}`;
          const hsl = rgbToHsl(r, g, b);

          // Favor colorful, visible paint colors over outlines/text.
          const vividness = 0.45 + hsl.s * 1.65;
          const lightPenalty = hsl.l > 0.9 || hsl.l < 0.08 ? 0.25 : 1;
          const weight = vividness * lightPenalty;
          buckets.set(key, (buckets.get(key) || 0) + weight);
        }

        let bestKey = "";
        let bestScore = -Infinity;
        for (const [key, score] of buckets.entries()) {
          if (score > bestScore) {
            bestScore = score;
            bestKey = key;
          }
        }

        if (!bestKey) {
          DOMINANT_LOGO_COLOR_CACHE.set(src, fallback);
          resolve(fallback);
          return;
        }

        let [r, g, b] = bestKey.split(",").map(Number);
        const hsl = rgbToHsl(r, g, b);

        // Keep very dark sampled paint visible on the dark chart theme.
        if (hsl.l < 0.20) {
          const lift = 0.20 / Math.max(hsl.l, 0.01);
          r = Math.min(255, Math.round(r * lift));
          g = Math.min(255, Math.round(g * lift));
          b = Math.min(255, Math.round(b * lift));
        }

        const color = `rgb(${r}, ${g}, ${b})`;
        DOMINANT_LOGO_COLOR_CACHE.set(src, color);
        resolve(color);
      } catch {
        DOMINANT_LOGO_COLOR_CACHE.set(src, fallback);
        resolve(fallback);
      }
    };

    img.onerror = () => {
      DOMINANT_LOGO_COLOR_CACHE.set(src, fallback);
      resolve(fallback);
    };

    img.src = src;
  });
}

function AutoColorBar({ logoSrc, x, y, width, height, fallbackColor, chartTheme }) {
  const [fill, setFill] = useState(fallbackColor);

  useEffect(() => {
    let cancelled = false;
    setFill(fallbackColor);

    if (!logoSrc) return () => { cancelled = true; };

    getDominantLogoColor(logoSrc, fallbackColor).then((color) => {
      if (!cancelled) setFill(color);
    });

    return () => {
      cancelled = true;
    };
  }, [logoSrc, fallbackColor]);

  const rectX = width >= 0 ? x : x + width;
  const rectW = Math.max(1.5, Math.abs(width));

  return (
    <rect
      x={rectX}
      y={y}
      width={rectW}
      height={height}
      rx={Math.min(8, height / 2)}
      ry={Math.min(8, height / 2)}
      fill={fill}
      opacity={chartTheme === "light" ? 0.92 : 0.88}
    />
  );
}

/** ======= TEAM LOGOS =======
 * Existing files live in: /public/logos/nascar/Teams/
 *
 * The folder contains a mix of naming styles (for example
 * Trackhouse_Racing.png, AM_RACING.png, cr7_motorsports.png), so team logos
 * intentionally try several filename variants before falling back to initials.
 */
const TEAM_LOGO_FILE_ALIASES = new Map([
  ["team reaume", "Team_Reaume.png"],
  ["team amerivet", "Team_AmeriVet.png"],
  ["trackhouse racing", "Trackhouse_Racing.png"],
  ["rick ware racing", "Rick_Ware_Racing.png"],
  ["akinori performance", "Akinori_Performance.png"],
  ["freedom racing enterprises", "Freedom_Racing_Enterprises.png"],
  ["halmar friesen racing", "Halmar_Friesen_Racing.png"],
  ["halmar-friesen racing", "Halmar_Friesen_Racing.png"],
  ["niece motorsports", "Niece_Motorsports.png"],
  ["rackley w a r", "Rackley_W.A.R.png"],
  ["rackley war", "Rackley_W.A.R.png"],
  ["mcanally hilgemann racing", "McAnally_hilgemann_Racing.png"],
  ["cr7 motorsports", "cr7_motorsports.png"],
  ["reaume brothers racing", "Reaume_Brothers_Racing.png"],
  ["viking motorsports", "Viking_Motorsports.png"],
  ["mike harmon racing", "mike_harmon_racing.png"],
  ["cope family racing", "Cope_Family_Racing.png"],
  ["am racing", "AM_RACING.png"],
  ["ss green light racing", "ss_green_light_racing.png"],
  ["rfk racing", "RFK_Racing.png"],
  ["legacy motor club", "Legacy_Motor_Club.png"],
  ["hyak motorsports", "HYAK_Motorsports.png"],
  ["garage 66", "Garage_66.png"],
]);

function normalizeTeamLogoKey(value) {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/['’]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** ======= CANONICAL TEAM NAMES =======
 * Historical/rebranded names that should aggregate as the same organization.
 * Keys use the same fuzzy normalization as the logo matcher, so capitalization,
 * punctuation, underscores, hyphens, and ampersands do not matter.
 */
const TEAM_NAME_ALIASES = new Map([
  // RFK Racing lineage / naming variants
  ["roush fenway keselowski racing", "RFK Racing"],
  ["roush fenway racing", "RFK Racing"],
  ["rfk racing", "RFK Racing"],
  ["rfk", "RFK Racing"],

  // Petty GMS rebranded to Legacy Motor Club
  ["petty gms", "Legacy Motor Club"],
  ["petty gms motorsports", "Legacy Motor Club"],
  ["legacy motor club", "Legacy Motor Club"],

  // JTG Daugherty Racing became HYAK Motorsports.
  // Keep the history together under the current organization.
  ["jtg daugherty racing", "HYAK Motorsports"],
  ["jtg daugherty", "HYAK Motorsports"],
  ["jtg daughety racing", "HYAK Motorsports"],
  ["jtg daughety", "HYAK Motorsports"],
  ["hyak motorsports", "HYAK Motorsports"],
  ["hyak", "HYAK Motorsports"],

  // Motorsports Business Management / MBM is now Garage 66.
  ["motorsports business management", "Garage 66"],
  ["motorsports business management llc", "Garage 66"],
  ["mbm motorsports", "Garage 66"],
  ["mbm", "Garage 66"],
  ["garage 66", "Garage 66"],

  // Common capitalization/spelling cleanup
  ["trackhouse racing", "Trackhouse Racing"],
  ["team penske", "Team Penske"],
  ["hendrick motorsports", "Hendrick Motorsports"],
  ["joe gibbs racing", "Joe Gibbs Racing"],
  ["23xi racing", "23XI Racing"],
]);

function canonicalTeamName(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const key = normalizeTeamLogoKey(raw);
  return TEAM_NAME_ALIASES.get(key) || raw;
}

function titleToken(token) {
  if (!token) return token;
  if (/^\d/.test(token)) return token.toUpperCase();
  if (/^[A-Z0-9]{2,5}$/.test(token)) return token;
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

function teamLogoCandidates(teamName) {
  const raw = String(teamName ?? "").trim();
  if (!raw) return [];

  const key = normalizeTeamLogoKey(raw);
  const candidates = [];
  const add = (file) => {
    const f = String(file ?? "").trim();
    if (!f) return;
    const withExt = /\.png$/i.test(f) ? f : `${f}.png`;
    const url = `/logos/nascar/Teams/${withExt}`;
    if (!candidates.includes(url)) candidates.push(url);
  };

  // Known oddball filenames always get first shot.
  add(TEAM_LOGO_FILE_ALIASES.get(key));

  const rawNoExt = raw.replace(/\.png$/i, "");
  const underscoredRaw = rawNoExt.replace(/\s+/g, "_");
  const cleanedWords = rawNoExt
    .replace(/&/g, " and ")
    .replace(/['’]/g, "")
    .replace(/[^a-zA-Z0-9.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);

  const titleWords = cleanedWords.map(titleToken);
  const lowerWords = cleanedWords.map((w) => w.toLowerCase());
  const upperWords = cleanedWords.map((w) => w.toUpperCase());

  // Most of your files follow one of these forms.
  add(underscoredRaw);
  add(titleWords.join("_"));
  add(lowerWords.join("_"));
  add(upperWords.join("_"));

  // Keep ampersand / punctuation variants available too.
  add(rawNoExt.replace(/\s+/g, "_"));
  add(rawNoExt.replace(/\s*&\s*/g, "_and_").replace(/\s+/g, "_"));
  add(rawNoExt.replace(/\s*&\s*/g, "_").replace(/\s+/g, "_"));

  // A few folders historically mixed capitalization inside the filename.
  if (titleWords.length) {
    for (let i = 0; i < titleWords.length; i++) {
      const mixed = [...titleWords];
      mixed[i] = mixed[i].toLowerCase();
      add(mixed.join("_"));
    }
  }

  // Common shortened data labels.
  const SHORT_TEAM_ALIASES = {
    "joe gibbs": "Joe_Gibbs_Racing.png",
    "hendrick": "Hendrick_Motorsports.png",
    "penske": "Team_Penske.png",
    "team penske": "Team_Penske.png",
    "rfk": "RFK_Racing.png",
    "rfk racing": "RFK_Racing.png",
    "23xi": "23XI_Racing.png",
    "23xi racing": "23XI_Racing.png",
    "front row": "Front_Row_Motorsports.png",
    "front row motorsports": "Front_Row_Motorsports.png",
    "legacy motor club": "Legacy_Motor_Club.png",
    "kaulig": "Kaulig_Racing.png",
    "kaulig racing": "Kaulig_Racing.png",
    "wood brothers": "Wood_Brothers_Racing.png",
    "wood brothers racing": "Wood_Brothers_Racing.png",
    "spires motorsports": "Spire_Motorsports.png",
    "spire motorsports": "Spire_Motorsports.png",
    "trackhouse": "Trackhouse_Racing.png",
  };
  add(SHORT_TEAM_ALIASES[key]);

  return candidates;
}

function TeamLogoMark({ teamName, size = 28, fallbackColor = "rgba(226,232,240,0.95)" }) {
  const candidates = useMemo(() => teamLogoCandidates(teamName), [teamName]);
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [teamName]);

  const src = candidates[candidateIndex] || "";

  if (!src) {
    return (
      <text
        x="0"
        y="4"
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fill={fallbackColor}
      >
        {String(teamName || "?").slice(0, 2).toUpperCase()}
      </text>
    );
  }

  return (
    <>
      <image
        href={src}
        x={-size / 2}
        y={-size / 2}
        width={size}
        height={size}
        preserveAspectRatio="xMidYMid meet"
        onError={() => {
          setCandidateIndex((idx) => (idx + 1 < candidates.length ? idx + 1 : candidates.length));
        }}
      />
      {candidateIndex >= candidates.length ? (
        <text
          x="0"
          y="4"
          textAnchor="middle"
          fontSize="9"
          fontWeight="700"
          fill={fallbackColor}
        >
          {String(teamName || "?").slice(0, 2).toUpperCase()}
        </text>
      ) : null}
    </>
  );
}

function spreadPoints(points, { r = 16, iters = 10, pad = 2 } = {}) {
  const out = points.map((p) => ({ ...p }));
  const minDist = r * 2 + pad;

  for (let k = 0; k < iters; k++) {
    let moved = false;
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i];
        const b = out[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy) || 0.0001;
        if (d < minDist) {
          const push = (minDist - d) / 2;
          const ux = dx / d;
          const uy = dy / d;
          a.x -= ux * push;
          a.y -= uy * push;
          b.x += ux * push;
          b.y += uy * push;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }

  return out;
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function parseNumericLike(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  if (/%$/.test(s)) return Number(s.replace(/%/g, ""));
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function stripDupSuffix(s) {
  return String(s).replace(/_\d+$/, "");
}

function keynorm(s) {
  return lower(String(s).replace(/[\s._%]/g, ""));
}

function dirForCol(c) {
  const k = keynorm(stripDupSuffix(c));

  if (
    k === "finish" ||
    k === "start" ||
    k === "avg" ||
    k === "avgrunningpos" ||
    k === "best" ||
    k === "bestpos" ||
    k === "mid" ||
    k === "midracepos" ||
    k === "worst" ||
    k === "worstpos" ||
    k === "gfsrank"
  ) {
    return "lower";
  }

  if (
    k === "dkpts" ||
    k === "fdpts" ||
    k === "rating" ||
    k === "driverrating" ||
    k === "pennrank" ||
    k === "gfs" ||
    k === "lapsled" ||
    k === "fastestlap" ||
    k === "fastestlaps" ||
    k === "wins" ||
    k === "top5" ||
    k === "top10" ||
    k === "top15" ||
    k === "top20"
  ) {
    return "higher";
  }

  return null;
}

/** ======= DK / FD SCORING ======= */
function dkFinishPts(finish) {
  const f = Math.round(toNum(finish));
  if (!f || f < 1) return 0;
  if (f === 1) return 45;
  if (f === 2) return 42;
  if (f === 3) return 41;
  if (f === 4) return 40;
  if (f === 5) return 39;
  if (f === 6) return 38;
  if (f === 7) return 37;
  if (f === 8) return 36;
  if (f === 9) return 35;
  if (f === 10) return 34;
  if (f === 11) return 32;
  if (f === 12) return 31;
  if (f === 13) return 30;
  if (f === 14) return 29;
  if (f === 15) return 28;
  if (f === 16) return 27;
  if (f === 17) return 26;
  if (f === 18) return 25;
  if (f === 19) return 24;
  if (f === 20) return 23;
  if (f === 21) return 21;
  if (f === 22) return 20;
  if (f === 23) return 19;
  if (f === 24) return 18;
  if (f === 25) return 17;
  if (f === 26) return 16;
  if (f === 27) return 15;
  if (f === 28) return 14;
  if (f === 29) return 13;
  if (f === 30) return 12;
  if (f === 31) return 10;
  if (f === 32) return 9;
  if (f === 33) return 8;
  if (f === 34) return 7;
  if (f === 35) return 6;
  if (f === 36) return 5;
  if (f === 37) return 4;
  if (f === 38) return 3;
  if (f === 39) return 2;
  if (f === 40) return 1;
  return 0;
}

function fdFinishPts(finish) {
  const f = Math.round(toNum(finish));
  if (!f || f < 1) return 0;
  if (f === 1) return 43;
  if (f === 2) return 40;
  if (f === 3) return 38;
  if (f === 4) return 37;
  if (f === 5) return 36;
  if (f === 6) return 35;
  if (f === 7) return 34;
  if (f === 8) return 33;
  if (f === 9) return 32;
  if (f === 10) return 31;
  if (f === 11) return 30;
  if (f === 12) return 29;
  if (f === 13) return 28;
  if (f === 14) return 27;
  if (f === 15) return 26;
  if (f === 16) return 25;
  if (f === 17) return 24;
  if (f === 18) return 23;
  if (f === 19) return 22;
  if (f === 20) return 21;
  if (f === 21) return 20;
  if (f === 22) return 19;
  if (f === 23) return 18;
  if (f === 24) return 17;
  if (f === 25) return 16;
  if (f === 26) return 15;
  if (f === 27) return 14;
  if (f === 28) return 13;
  if (f === 29) return 12;
  if (f === 30) return 11;
  if (f === 31) return 10;
  if (f === 32) return 9;
  if (f === 33) return 8;
  if (f === 34) return 7;
  if (f === 35) return 6;
  if (f === 36) return 5;
  if (f === 37) return 4;
  if (f === 38) return 3;
  if (f === 39) return 2;
  if (f === 40) return 1;
  return 0;
}

function calcDkPts({ finish, start, fastest_lap, laps_led }) {
  const fin = toNum(finish);
  const st = toNum(start);
  const pd = st && fin ? st - fin : 0;
  return dkFinishPts(fin) + pd * 1 + toNum(fastest_lap) * 0.45 + toNum(laps_led) * 0.25;
}

function calcFdPts({ finish, start, laps, laps_led }) {
  const fin = toNum(finish);
  const st = toNum(start);
  const pd = st && fin ? st - fin : 0;
  return fdFinishPts(fin) + pd * 0.5 + toNum(laps) * 0.1 + toNum(laps_led) * 0.1;
}

/** identity column detection for sticky freeze */
function isDriverCol(c, col) {
  const k = lower(String(c).trim());
  const driverKey = lower(String(col.driver || "").trim());
  return k === driverKey || k === "driver_name";
}

function isPrimaryStickyCol(c, col, mode, aggGroupBy) {
  // Single Race keeps Driver frozen like before.
  if (mode !== "agg") {
    return isDriverCol(c, col);
  }

  const k = lower(String(c).trim());

  // Aggregate mode freezes whichever identity the user grouped by.
  if (aggGroupBy === "team") {
    const teamKey = lower(String(col.team || "").trim());
    return k === "team" || (teamKey && k === teamKey);
  }

  if (aggGroupBy === "car") {
    const carKey = lower(String(col.car || "").trim());
    return k === "car" || (carKey && k === carKey);
  }

  // Aggregate By Driver
  return isDriverCol(c, col);
}

function MultiTrackDropdown({ options, values, onChange }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const el = e.target;
      // close if click outside the dropdown root
      if (!el?.closest?.("[data-multitrack-root='1']")) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const label = useMemo(() => {
    if (!values.length) return "All";
    if (values.length === 1) return values[0];
    return `${values.length} selected`;
  }, [values]);

  function toggleValue(v) {
    if (v === "__ALL__") {
      onChange([]);
      return;
    }
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  }

  return (
    <div className="relative w-[min(18rem,calc(100vw-4.5rem))] sm:w-auto" data-multitrack-root="1">
      <button
        type="button"
        className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 w-full sm:w-[26rem] flex items-center justify-between"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Select one or more tracks"
      >
        <span className="truncate">{label}</span>
        <span className="text-slate-300">▾</span>
      </button>

      {open ? (
        <div className="absolute mt-1 w-full sm:w-[26rem] max-h-72 overflow-auto rounded-md border border-slate-700 bg-slate-950 shadow-2xl z-50">
          <div
            className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-900 flex items-center gap-2"
            onClick={() => toggleValue("__ALL__")}
            role="option"
            aria-selected={!values.length}
          >
            <input type="checkbox" readOnly checked={!values.length} />
            <span>All</span>
          </div>

          <div className="h-px bg-slate-800" />

          {options.map((t) => {
            const checked = values.includes(t);
            return (
              <div
                key={t}
                className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-900 flex items-center gap-2"
                onClick={() => toggleValue(t)}
                role="option"
                aria-selected={checked}
              >
                <input type="checkbox" readOnly checked={checked} />
                <span className="truncate">{t}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function MultiYearDropdown({ options, values, onChange }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const el = e.target;
      if (!el?.closest?.("[data-multiyear-root='1']")) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const label = useMemo(() => {
    if (!values.length) return "All";
    if (values.length === 1) return values[0];
    return `${values.length} selected`;
  }, [values]);

  function toggleValue(v) {
    if (v === "__ALL__") {
      onChange([]);
      return;
    }
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  }

  return (
    <div className="relative" data-multiyear-root="1">
      <button
        type="button"
        className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 w-44 flex items-center justify-between"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Select one or more years"
      >
        <span className="truncate">{label}</span>
        <span className="text-slate-300">▾</span>
      </button>

      {open ? (
        <div className="absolute mt-1 w-44 max-h-72 overflow-auto rounded-md border border-slate-700 bg-slate-950 shadow-2xl z-50">
          <div
            className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-900 flex items-center gap-2"
            onClick={() => toggleValue("__ALL__")}
            role="option"
            aria-selected={!values.length}
          >
            <input type="checkbox" readOnly checked={!values.length} />
            <span>All</span>
          </div>

          <div className="h-px bg-slate-800" />

          {options.map((y) => {
            const checked = values.includes(y);
            return (
              <div
                key={y}
                className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-900 flex items-center gap-2"
                onClick={() => toggleValue(y)}
                role="option"
                aria-selected={checked}
              >
                <input type="checkbox" readOnly checked={checked} />
                <span className="truncate">{y}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function MultiRaceDropdown({ options, values, onChange }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const el = e.target;
      if (!el?.closest?.("[data-multirace-root='1']")) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const label = useMemo(() => {
    if (!values.length) return "Select races…";
    if (values.length === 1) {
      const hit = options.find((o) => o.value === values[0]);
      return hit ? hit.label : "1 selected";
    }
    return `${values.length} selected`;
  }, [values, options]);

  function toggleValue(v) {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  }

  function clearAll() {
    onChange([]);
  }

  return (
    <div className="relative w-[min(18rem,calc(100vw-4.5rem))] sm:w-auto" data-multirace-root="1">
      <button
        type="button"
        className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 w-full sm:w-[30rem] flex items-center justify-between"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Select one or more races"
      >
        <span className="truncate">{label}</span>
        <span className="text-slate-300">▾</span>
      </button>

      {open ? (
        <div className="absolute mt-1 w-full sm:w-[30rem] max-h-80 overflow-auto rounded-md border border-slate-700 bg-slate-950 shadow-2xl z-50">
          <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-between">
            <span>Pick races (multi-select)</span>
            <button type="button" className="text-sky-300 hover:text-sky-200" onClick={clearAll} title="Clear selection">
              Clear
            </button>
          </div>

          <div className="h-px bg-slate-800" />

          {options.map((o) => {
            const checked = values.includes(o.value);
            return (
              <div
                key={o.value}
                className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-900 flex items-start gap-2"
                onClick={() => toggleValue(o.value)}
                role="option"
                aria-selected={checked}
              >
                <input className="mt-[2px]" type="checkbox" readOnly checked={checked} />
                <span className="truncate">{o.label}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** Track Type dropdown (multi-select) */
function MultiTrackTypeDropdown({ options, values, onChange }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const el = e.target;
      if (!el?.closest?.("[data-multitype-root='1']")) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const label = useMemo(() => {
    if (!values.length) return "All";
    if (values.length === 1) return prettyHeader(values[0]);
    return `${values.length} selected`;
  }, [values]);

  function toggleValue(v) {
    if (v === "__ALL__") {
      onChange([]);
      return;
    }
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  }

  return (
    <div className="relative" data-multitype-root="1">
      <button
        type="button"
        className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 w-56 flex items-center justify-between"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Select one or more track types"
      >
        <span className="truncate">{label}</span>
        <span className="text-slate-300">▾</span>
      </button>

      {open ? (
        <div className="absolute mt-1 w-56 max-h-72 overflow-auto rounded-md border border-slate-700 bg-slate-950 shadow-2xl z-50">
          <div
            className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-900 flex items-center gap-2"
            onClick={() => toggleValue("__ALL__")}
            role="option"
            aria-selected={!values.length}
          >
            <input type="checkbox" readOnly checked={!values.length} />
            <span>All</span>
          </div>

          <div className="h-px bg-slate-800" />

          {options.map((t) => {
            const checked = values.includes(t);
            return (
              <div
                key={t}
                className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-900 flex items-center gap-2"
                onClick={() => toggleValue(t)}
                role="option"
                aria-selected={checked}
              >
                <input type="checkbox" readOnly checked={checked} />
                <span className="truncate">{prettyHeader(t)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** Track Type help dropdown */
function TrackTypeHelpDropdown() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const el = e.target;
      if (!el?.closest?.("[data-tracktype-help-root='1']")) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="relative" data-tracktype-help-root="1">
      <button
        type="button"
        className="border border-slate-700 rounded-md px-3 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 h-[38px] whitespace-nowrap"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="View tracks in each track type"
      >
        Help
      </button>

      {open ? (
        <div className="fixed right-4 top-36 w-[min(30rem,calc(100vw-2rem))] max-h-[70vh] overflow-auto rounded-md border border-slate-700 bg-slate-950 shadow-2xl z-[9999] p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-100">Track Type Guide</div>
            <button
              type="button"
              className="text-[11px] text-slate-400 hover:text-slate-200"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>

          <div className="space-y-3">
            {Object.entries(TRACK_GROUPS).map(([typeKey, tracks]) => (
              <div
                key={typeKey}
                className="rounded-md border border-slate-800 bg-slate-900/60 p-3"
              >
                <div className="mb-2 text-xs font-semibold text-sky-300">
                  {prettyHeader(typeKey)}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {tracks.map((track) => (
                    <span
                      key={track}
                      className="inline-flex items-center rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-200"
                    >
                      {track}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** ======= PENN RANK ======= */
const PENN_RANK_CONFIG = {
  agg: [
    { key: "finish", weight: 0.098, reverse: true },
    { key: "fastest_lap", weight: 0.196 },
    { key: "laps_led", weight: 0.147 },
    { key: "top_15_laps", weight: 0.0 },
    { key: "pct_top_15_laps", weight: 0.098 },
    { key: "green_flag_passing_diff", weight: 0.098 },
    { key: "pct_quality_passes", weight: 0.049 },
    { key: "quality_passes", weight: 0.0 },
    { key: "green_flag_times_passed", weight: 0.0196, reverse: true },
    { key: "rating", weight: 0.098 },
    { key: "avg", weight: 0.196, reverse: true },
  ],

  race: [
    { key: "finish", weight: 0.098, reverse: true },
    { key: "fastest_lap", weight: 0.196 },
    { key: "laps_led", weight: 0.147 },
    { key: "top_15_laps", weight: 0.0 },
    { key: "pct_top_15_laps", weight: 0.098 },
    { key: "green_flag_passing_diff", weight: 0.098 },
    { key: "pct_quality_passes", weight: 0.049 },
    { key: "quality_passes", weight: 0.0 },
    { key: "green_flag_times_passed", weight: 0.0196, reverse: true },
    { key: "rating", weight: 0.098 },
    { key: "avg", weight: 0.196, reverse: true },
  ],
  };

function getPennRankConfig(mode) {
  return mode === "agg" ? PENN_RANK_CONFIG.agg : PENN_RANK_CONFIG.race;
}

function getPennMetricValue(row, metricKey, col) {
  const direct = row?.[metricKey];
  if (direct != null && direct !== "" && Number.isFinite(Number(direct))) {
    return Number(direct);
  }

  const mappedKey =
    metricKey === "finish" ? col.finish :
    metricKey === "start" ? col.start :
    metricKey === "avg" ? col.avg :
    metricKey === "rating" ? col.rating :
    metricKey === "fastest_lap" ? col.fl :
    metricKey === "top_15_laps" ? col.t15 :
    metricKey === "pct_top_15_laps" ? col.pt15 :
    metricKey === "laps_led" ? col.ll :
    metricKey === "pct_laps_led" ? col.pll :
    metricKey === "green_flag_passing_diff" ? col.gfpd :
    metricKey === "green_flag_passes" ? col.gfp :
    metricKey === "green_flag_times_passed" ? col.gftp :
    metricKey === "quality_passes" ? col.qp :
    metricKey === "pct_quality_passes" ? col.pqp :
    "";

  if (!mappedKey) return null;

  const v = row?.[mappedKey];
  if (v == null || v === "") return null;

  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function buildPennRank(rows, mode, col) {
  if (!Array.isArray(rows) || !rows.length) return rows;

  const config = getPennRankConfig(mode);

  const metricStats = config.map((m) => {
    const vals = rows
      .map((r) => getPennMetricValue(r, m.key, col))
      .filter((v) => v != null && Number.isFinite(v));

    return {
      ...m,
      min: vals.length ? Math.min(...vals) : null,
      max: vals.length ? Math.max(...vals) : null,
    };
  });

  return rows.map((row) => {
    let weightedSum = 0;
    let usedWeight = 0;

    for (const m of metricStats) {
      const raw = getPennMetricValue(row, m.key, col);
      if (raw == null || !Number.isFinite(raw)) continue;
      if (m.min == null || m.max == null) continue;

      let scaled = 0.5;

      if (m.max !== m.min) {
        scaled = m.reverse
          ? (m.max - raw) / (m.max - m.min)
          : (raw - m.min) / (m.max - m.min);
      }

      weightedSum += scaled * m.weight;
      usedWeight += m.weight;
    }

    const pennRank = usedWeight > 0 ? (weightedSum / usedWeight) * 100 : "";

    return {
      ...row,
      penn_rank: pennRank === "" ? "" : Math.round(pennRank * 10) / 10,
    };
  });
}

/** ======= PAGE ======= */
export default function NascarDataExplorer() {
  const [rows, setRows] = useState([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [activeRows, setActiveRows] = useState([]);
  const [view, setView] = useState("table"); // table | charts
  const [xCol, setXCol] = useState("");
  const [yCol, setYCol] = useState("");
  const [revX, setRevX] = useState(null);
  const [revY, setRevY] = useState(null);
  const [chartTheme, setChartTheme] = useState("dark"); // dark | light
  const chartSvgRef = useRef(null);
  const [savingPdf, setSavingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");
  async function handleSavePdf() {
    if (savingPdf) return;
    setSavingPdf(true);
    setPdfError("");
    try {
      const stat = [xCol, yCol].filter(Boolean).map(prettyHeader).join("-vs-");
      const filename = `nascar-${stat}-${new Date().toISOString().slice(0, 10)}.pdf`
        .replace(/[^a-zA-Z0-9._-]+/g, "-");
      await saveChartPdf(chartSvgRef.current, filename, chartTheme === "light" ? "#ffffff" : "#0f172a");
    } catch (error) {
      setPdfError(error?.message || "Unable to save the chart. Please try again.");
    } finally {
      setSavingPdf(false);
    }
  }
  const [barLimit, setBarLimit] = useState(10); // 0 = show all ranked bars

  // Track History removed
  const MODES = [
    { key: "race", label: "Single Race" },
    { key: "agg", label: "Aggregate (Recent N Races)" },
  ];
  const [mode, setMode] = useState("race");

  // filters
  const [series, setSeries] = useState("All");
  const [track, setTrack] = useState("All");
  const [tracks, setTracks] = useState([]); // Aggregate mode multi-track
  const [trackTypes, setTrackTypes] = useState([]); // Aggregate mode track type(s)
  const [years, setYears] = useState([]); // [] = All
  const [raceId, setRaceId] = useState("");
  const [lastN, setLastN] = useState(6);
  const [aggScope, setAggScope] = useState("global"); // "global" | "per_track"
  const [aggSource, setAggSource] = useState("recent"); // "recent" | "races"
  const [aggRaceIds, setAggRaceIds] = useState([]); // selected race ids/keys for aggSource="races"
  const [aggGroupBy, setAggGroupBy] = useState("driver"); // driver | car | team
  const [excludeExhibition, setExcludeExhibition] = useState(true);
  // table controls
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "", dir: "asc" });

  // Race Log modal (Aggregate Driver / Car rows)
  const [raceLogTarget, setRaceLogTarget] = useState(null);
  const [raceLogScope, setRaceLogScope] = useState("filtered"); // filtered | full

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const data = await fetchJson(`${BASE}/${DATA_FILE}`);
        if (dead) return;

        const normalizedRows = (Array.isArray(data) ? data : []).map((r) => {
          const laps = toNum(r?.LapsCompleted ?? r?.laps);
          const top15 = toNum(r?.Top15Laps ?? r?.top_15_laps);
          const led = toNum(r?.LapsLed ?? r?.laps_led);
          const quality = toNum(r?.QualityPasses ?? r?.quality_passes);
          const gfPasses = toNum(r?.GreenFlagPasses ?? r?.green_flag_passes);

          return {
            ...r,
            pct_top_15_laps: laps > 0 ? (top15 / laps) * 100 : "",
            pct_laps_led: laps > 0 ? (led / laps) * 100 : "",
            pct_quality_passes: gfPasses > 0 ? (quality / gfPasses) * 100 : "",
          };
        });

        setRows(normalizedRows);
        try {
          const u = await fetchJson(`${BASE}/updated.json`);
          if (u?.last_updated) setUpdatedAt(u.last_updated);
        } catch {}
      } catch (e) {
        if (!dead) setErr(String(e?.message || e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);

  useEffect(() => {
  let dead = false;

  async function loadActiveRows() {
    try {
      let files = [];

      if (series === "All") {
        files = [
          ACTIVE_DATA_SOURCES.cup,
          ACTIVE_DATA_SOURCES.xfinity,
          ACTIVE_DATA_SOURCES.trucks,
        ];
      } else {
        const bucket = seriesBucket(series);
        if (bucket === "xfinity") files = [ACTIVE_DATA_SOURCES.xfinity];
        else if (bucket === "trucks") files = [ACTIVE_DATA_SOURCES.trucks];
        else files = [ACTIVE_DATA_SOURCES.cup];
      }

      const results = await Promise.all(
        files.map((f) => fetchJson(f).catch(() => []))
      );

      if (dead) return;

      const merged = results.flatMap((result, index) => {
        const file = files[index] || "";
        const bucket =
          file === ACTIVE_DATA_SOURCES.xfinity
            ? "xfinity"
            : file === ACTIVE_DATA_SOURCES.trucks
            ? "trucks"
            : "cup";

        return (Array.isArray(result) ? result : []).filter(Boolean).map((row) => ({
          ...row,
          __activeSeriesBucket: bucket,
        }));
      });
      setActiveRows(merged);
    } catch {
      if (!dead) setActiveRows([]);
    }
  }

  loadActiveRows();

  return () => {
    dead = true;
  };
}, [series]);

  const allColumnsRaw = useMemo(() => (rows?.[0] ? Object.keys(rows[0]) : []), [rows]);

  const col = useMemo(() => {
    const date = pickCol(allColumnsRaw, ["RaceDate", "date", "race_date"]);
    const series = pickCol(allColumnsRaw, ["Series", "series", "series_name"]);
    const track = pickCol(allColumnsRaw, ["Track", "track", "track_name", "venue"]);
    const race = pickCol(allColumnsRaw, ["RaceName", "race", "race_name", "event", "event_name"]);
    const race_id = pickCol(allColumnsRaw, ["RaceID", "race_id", "raceid"]);
    const driver = pickCol(allColumnsRaw, ["Driver", "driver", "driver_name", "name"]);
    const driver_id = pickCol(allColumnsRaw, ["DriverID", "driver_id"]);

    const car = pickCol(allColumnsRaw, ["Car", "car", "car_number"]);
    const team = pickCol(allColumnsRaw, ["Team", "team", "team_name"]);
    const manufacturer = pickCol(allColumnsRaw, ["Manufacturer", "manufacturer", "make"]);
    const sponsor = pickCol(allColumnsRaw, ["Sponsor", "sponsor"]);
    const crewChief = pickCol(allColumnsRaw, ["CrewChief", "crew_chief", "crew_chief_fullname"]);

    const finish = pickCol(allColumnsRaw, ["Finish", "finish", "finish_position", "fin"]);
    const start = pickCol(allColumnsRaw, ["Start", "start", "start_position"]);
    const mid = pickCol(allColumnsRaw, ["MidRacePos", "mid", "mid_race_pos"]);
    const best = pickCol(allColumnsRaw, ["BestPos", "best", "best_pos"]);
    const worst = pickCol(allColumnsRaw, ["WorstPos", "worst", "worst_pos"]);
    const avg = pickCol(allColumnsRaw, ["AvgRunningPos", "avg", "avg_running_pos"]);

    const gfs = pickCol(allColumnsRaw, ["GFS", "gfs"]);
    const gfsRank = pickCol(allColumnsRaw, ["GFSRank", "gfs_rank"]);
    const gfLaps = pickCol(allColumnsRaw, ["GFLaps", "gf_laps"]);

    const gfpd = pickCol(allColumnsRaw, ["PassDifferential", "green_flag_passing_diff", "pass_differential"]);
    const gfp = pickCol(allColumnsRaw, ["GreenFlagPasses", "green_flag_passes"]);
    const gftp = pickCol(allColumnsRaw, ["GreenFlagPassed", "green_flag_times_passed", "green_flag_passed"]);
    const qp = pickCol(allColumnsRaw, ["QualityPasses", "quality_passes"]);
    const pqp = pickCol(allColumnsRaw, ["pct_quality_passes"]);

    const fl = pickCol(allColumnsRaw, ["FastestLaps", "fastest_lap", "fastest_laps", "fast_laps", "fast_lap"]);
    const t15 = pickCol(allColumnsRaw, ["Top15Laps", "top_15_laps"]);
    const pt15 = pickCol(allColumnsRaw, ["pct_top_15_laps"]);

    const ll = pickCol(allColumnsRaw, ["LapsLed", "laps_led"]);
    const pll = pickCol(allColumnsRaw, ["pct_laps_led"]);
    const laps = pickCol(allColumnsRaw, ["LapsCompleted", "laps", "laps_completed"]);

    const rating = pickCol(allColumnsRaw, ["DriverRating", "rating", "driver_rating"]);
    const cautions = pickCol(allColumnsRaw, ["Cautions", "cautions"]);
    const dnf = pickCol(allColumnsRaw, ["DNF", "dnf"]);
    const status = pickCol(allColumnsRaw, ["Status", "status", "finishing_status"]);

    return {
      date,
      series,
      track,
      race,
      race_id,
      driver,
      driver_id,
      car,
      team,
      manufacturer,
      sponsor,
      crewChief,
      finish,
      start,
      mid,
      best,
      worst,
      avg,
      gfs,
      gfsRank,
      gfLaps,
      gfpd,
      gfp,
      gftp,
      qp,
      pqp,
      fl,
      t15,
      pt15,
      ll,
      pll,
      laps,
      rating,
      cautions,
      dnf,
      status,
    };
  }, [allColumnsRaw]);

  const seriesOptions = useMemo(() => {
    if (!col.series) return ["All"];
    const set = new Set(rows.map((r) => r?.[col.series]).filter(Boolean).map(String));
    return ["All", ...Array.from(set).sort()];
  }, [rows, col.series]);

  // Track types (from your TRACK_GROUPS)
  const trackTypeOptions = useMemo(() => Object.keys(TRACK_GROUPS).sort(), []);

  // All track names present in data
  const allTrackOptions = useMemo(() => {
    if (!col.track) return ["All"];
    const set = new Set(rows.map((r) => r?.[col.track]).filter(Boolean).map(String));
    return ["All", ...Array.from(set).sort()];
  }, [rows, col.track]);

  // Track options shown in the Track dropdown (filtered by trackTypes in agg mode)
  const trackOptions = useMemo(() => {
    if (mode !== "agg" || !trackTypes.length || !col.track) return allTrackOptions;
    const only = allTrackOptions
      .filter((t) => t !== "All")
      .filter((t) => matchesTrackTypes(t, trackTypes));
    return ["All", ...only];
  }, [allTrackOptions, trackTypes, mode, col.track]);

  const yearOptions = useMemo(() => {
    const set = new Set();
    for (const r of rows) {
      const y = col.date ? safeYearFromDate(r?.[col.date]) : "";
      if (y) set.add(y);
    }
    return ["All", ...Array.from(set).sort().reverse()];
  }, [rows, col.date]);

    const baseFiltered = useMemo(() => {
    let out = rows;

    if (series !== "All" && col.series) out = out.filter((r) => String(r?.[col.series] ?? "") === series);

    // Exclude exhibition races (Cup only) by RaceName.
    // Matches Clash, Duel, and All Star / All-Star races automatically.
    if (excludeExhibition && col.series && col.race) {
      out = out.filter((r) => {
        const seriesName = String(r?.[col.series] ?? "");
        const raceName = String(r?.[col.race] ?? "");
        return !isExhibitionRace(seriesName, raceName);
      });
    }

    // Track Type filter (agg mode only)
    if (mode === "agg" && trackTypes.length && col.track) {
      out = out.filter((r) => matchesTrackTypes(String(r?.[col.track] ?? ""), trackTypes));
    }

    // Track filter
    if (col.track) {
      if (mode === "race") {
        if (track !== "All") out = out.filter((r) => String(r?.[col.track] ?? "") === track);
      } else {
        if (tracks.length) out = out.filter((r) => tracks.includes(String(r?.[col.track] ?? "")));
      }
    }

    if (years.length) {
      out = out.filter((r) => years.includes(safeYearFromDate(col.date ? r?.[col.date] : "")));
    }

    return out;
  }, [rows, series, track, tracks, years, mode, trackTypes, excludeExhibition, col.series, col.race, col.date, col.track]);

  const raceOptions = useMemo(() => {
    const getRid = (r) => {
      if (col.race_id) return String(r?.[col.race_id] ?? "");
      const d = col.date ? String(r?.[col.date] ?? "") : "";
      const s = col.series ? publicSeriesLabel(r?.[col.series]) : "";
      const t = col.track ? String(r?.[col.track] ?? "") : "";
      const rn = col.race ? String(r?.[col.race] ?? "") : "";
      return [s, d, t, rn].filter(Boolean).join("|");
    };

    const map = new Map();
    for (const r of baseFiltered) {
      const rid = getRid(r);
      if (!rid) continue;
      if (!map.has(rid)) {
        const d = col.date ? String(r?.[col.date] ?? "") : "";
        const s = col.series ? publicSeriesLabel(r?.[col.series]) : "";
        const t = col.track ? String(r?.[col.track] ?? "") : "";
        const rn = col.race ? String(r?.[col.race] ?? "") : "";
        const displayDate = d ? formatEasternRaceDate(d) : "????-??-??";
        const label = `${displayDate} • ${s || "Series"} • ${t || "Track"}${rn ? ` • ${rn}` : ""}`;
        map.set(rid, { value: rid, label, dateSort: d || "" });
      }
    }
    return Array.from(map.values()).sort((a, b) => String(b.dateSort).localeCompare(String(a.dateSort)));
  }, [baseFiltered, col]);

const aggRaceOptions = useMemo(() => {
  const getRid = (r) => {
    if (col.race_id) return String(r?.[col.race_id] ?? "");
    const d = col.date ? String(r?.[col.date] ?? "") : "";
    const s = col.series ? String(r?.[col.series] ?? "") : "";
    const t = col.track ? String(r?.[col.track] ?? "") : "";
    const rn = col.race ? String(r?.[col.race] ?? "") : "";
    return [s, d, t, rn].filter(Boolean).join("|");
  };

  const map = new Map();
  for (const r of baseFiltered) {
    const rid = getRid(r);
    if (!rid) continue;
    if (!map.has(rid)) {
      const d = col.date ? String(r?.[col.date] ?? "") : "";
      const s = col.series ? publicSeriesLabel(r?.[col.series]) : "";
      const t = col.track ? String(r?.[col.track] ?? "") : "";
      const rn = col.race ? String(r?.[col.race] ?? "") : "";
      const label = `${d || "????-??-??"} • ${s || "Series"} • ${t || "Track"}${rn ? ` • ${rn}` : ""}`;
      map.set(rid, { value: rid, label, dateSort: d || "" });
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    String(b.dateSort).localeCompare(String(a.dateSort))
  );
}, [baseFiltered, col]);

const maxAggN = useMemo(() => {
  const getRaceKey = (r) => {
    if (col.race_id) return String(r?.[col.race_id] ?? "");
    const d = col.date ? String(r?.[col.date] ?? "") : "";
    const s = col.series ? String(r?.[col.series] ?? "") : "";
    const t = col.track ? String(r?.[col.track] ?? "") : "";
    const rn = col.race ? String(r?.[col.race] ?? "") : "";
    return [s, d, t, rn].filter(Boolean).join("|");
  };

  if (aggSource === "races") return aggRaceOptions.length;

  if (mode === "agg" && aggScope === "per_track" && tracks.length && col.track) {
    const counts = tracks
      .map((trackName) => {
        const seen = new Set();
        for (const r of baseFiltered) {
          if (String(r?.[col.track] ?? "") !== trackName) continue;
          const key = getRaceKey(r);
          if (key) seen.add(key);
        }
        return seen.size;
      })
      .filter((n) => n > 0);

    return counts.length ? Math.min(...counts) : 0;
  }

  const seen = new Set();
  for (const r of baseFiltered) {
    const key = getRaceKey(r);
    if (key) seen.add(key);
  }
  return seen.size;
}, [aggRaceOptions.length, aggSource, aggScope, baseFiltered, tracks, mode, col]);

useEffect(() => {
  if (mode !== "agg") return;
  if (aggSource !== "recent") return;

  setLastN(Math.max(1, maxAggN));
}, [mode, aggSource, maxAggN]);

useEffect(() => {
  if (mode !== "race") return;
  if (!raceOptions.length) {
    if (raceId) setRaceId("");
    return;
  }
  const exists = raceId && raceOptions.some((r) => r.value === raceId);
  if (!exists) setRaceId(raceOptions[0].value);
}, [mode, raceId, raceOptions]);

  const dateDesc = (a, b) => {
    const ad = col.date ? String(a?.[col.date] ?? "") : "";
    const bd = col.date ? String(b?.[col.date] ?? "") : "";
    return bd.localeCompare(ad);
  };

  const modeRows = useMemo(() => {
    const getRaceKey = (r) => {
      if (col.race_id) return String(r?.[col.race_id] ?? "");
      const d = col.date ? String(r?.[col.date] ?? "") : "";
      const s = col.series ? publicSeriesLabel(r?.[col.series]) : "";
      const t = col.track ? String(r?.[col.track] ?? "") : "";
      const rn = col.race ? String(r?.[col.race] ?? "") : "";
      return [s, d, t, rn].filter(Boolean).join("|");
    };

    if (mode === "race") {
      if (!raceId) return [];

      const raceRows = col.race_id
        ? baseFiltered.filter(
            (r) => String(r?.[col.race_id] ?? "") === raceId
          )
        : baseFiltered.filter(
            (r) => getRaceKey(r) === raceId
          );

      // Normalize NASCAR's occasional 3-digit encoded car numbers.
      // Examples:
      // 133 -> 33
      // 135 -> 35
      // 101 -> 01
      // 107 -> 07
      return raceRows.map((r) => {
        if (!col.car) return r;

        return {
          ...r,
          [col.car]: normalizeDisplayCarNumber(r?.[col.car]),
        };
      });
    }

    // ===== AGGREGATE =====
    if (aggSource === "races" && !(aggRaceIds && aggRaceIds.length)) return [];
    const sorted = [...baseFiltered].sort(dateDesc);

    let pickedRaceKeys = [];

    if (aggSource === "races") {
      pickedRaceKeys = (aggRaceIds || []).filter(Boolean);
    } else if (aggScope === "per_track" && tracks.length && col.track) {
      const perTrack = new Map();
      for (const t of tracks) perTrack.set(t, []);
      const seenByTrack = new Map();
      for (const t of tracks) seenByTrack.set(t, new Set());

      for (const r of sorted) {
        const tval = String(r?.[col.track] ?? "");
        if (!perTrack.has(tval)) continue;
        const key = getRaceKey(r);
        if (!key) continue;
        const seen = seenByTrack.get(tval);
        if (seen.has(key)) continue;
        seen.add(key);
        const arr = perTrack.get(tval);
        arr.push(key);

        let done = true;
        for (const [, a] of perTrack) {
          if (a.length < Number(lastN || 0)) {
            done = false;
            break;
          }
        }
        if (done) break;
      }

      const flat = [];
      for (const a of perTrack.values()) flat.push(...a);
      pickedRaceKeys = Array.from(new Set(flat));
    } else {
      const seenRaces = new Set();
      for (const r of sorted) {
        const key = getRaceKey(r);
        if (!key) continue;
        if (!seenRaces.has(key)) {
          seenRaces.add(key);
          pickedRaceKeys.push(key);
          if (pickedRaceKeys.length >= Number(lastN || 0)) break;
        }
      }
    }

    const pickedRows = baseFiltered.filter((r) => pickedRaceKeys.includes(getRaceKey(r)));

    const avgFields = [
      col.finish,
      col.start,
      col.mid,
      col.avg,
      col.gfsRank,
      col.gfLaps,
      col.gfpd,
      col.pqp,
      col.fl,
      col.pt15,
      col.ll,
      col.pll,
      col.rating,
      col.cautions,
    ].filter(Boolean);

    const sumFields = [col.gfp, col.gftp, col.qp, col.t15].filter(Boolean);

    // Maximum laps completed in each selected race = that race's total distance.
    // Used to calculate aggregate % Laps Completed instead of displaying raw lap totals.
    const raceTotalLaps = new Map();
    if (col.laps) {
      for (const r of pickedRows) {
        const raceKey = getRaceKey(r);
        if (!raceKey) continue;
        const completed = toNum(r?.[col.laps]);
        const previous = raceTotalLaps.get(raceKey) || 0;
        if (completed > previous) raceTotalLaps.set(raceKey, completed);
      }
    }
    const bestField = col.best;
    const worstField = col.worst;

    // Group key rules:
    // Driver = driver name
    // Car    = series + car number, so Cup #2 and Trucks #2 never merge
    // Team   = series + team, so organizations in different series never merge
    const getGroupInfo = (r) => {
      const seriesName = col.series ? String(r?.[col.series] ?? "").trim() : "";
      const rawDriverName = col.driver ? String(r?.[col.driver] ?? "").trim() : "";
      const driverName = cleanDriverDisplayName(rawDriverName);
      const driverGroupKey = canonicalDriverKey(driverName, { stripSuffix: false });
      const carNumber = col.car
        ? normalizeDisplayCarNumber(r?.[col.car])
        : "";
      const rawTeamName = col.team ? String(r?.[col.team] ?? "").trim() : "";
      const teamName = aggGroupBy === "team"
        ? canonicalTeamName(rawTeamName)
        : rawTeamName;

      if (aggGroupBy === "car") {
        if (!carNumber) return null;
        return {
          key: `${seriesName}|car|${carNumber}`,
          label: carNumber,
          driverName,
          carNumber,
          teamName,
          seriesName,
        };
      }

      if (aggGroupBy === "team") {
        if (!teamName) return null;
        return {
          key: `${seriesName}|team|${teamName}`,
          label: teamName,
          driverName,
          carNumber,
          teamName,
          seriesName,
        };
      }

      if (!driverName || !driverGroupKey) return null;
      return {
        // Group by normalized driver identity, NOT car number.
        // A driver who used #88 for two races and #97 later still has one row.
        key: `${seriesName}|driver|${driverGroupKey}`,
        label: driverName,
        driverName,
        carNumber,
        teamName,
        seriesName,
      };
    };

    const map = new Map();

    for (const r of pickedRows) {
      const info = getGroupInfo(r);
      if (!info) continue;

      if (!map.has(info.key)) {
        map.set(info.key, {
          group_label: info.label,
          driver_name: aggGroupBy === "driver" ? info.driverName : undefined,
          car: aggGroupBy === "car" ? info.carNumber : undefined,
          team: aggGroupBy === "team" ? info.teamName : undefined,
          manufacturer: col.manufacturer ? r?.[col.manufacturer] : undefined,
          series: info.seriesName || undefined,
          races_count: 0,
          entries_count: 0,
          __race_keys: new Set(),
          __identity_date: "",
          __sum: Object.fromEntries([...avgFields, ...sumFields].map((f) => [f, 0])),
          __cnt: Object.fromEntries(avgFields.map((f) => [f, 0])),
          __best: bestField ? Infinity : undefined,
          __worst: worstField ? -Infinity : undefined,
          __dk_sum: 0,
          __fd_sum: 0,
          __pts_cnt: 0,
          __laps_completed_sum: 0,
          __possible_laps_sum: 0,
          __win: 0,
          __top5: 0,
          __top10: 0,
          __top15: 0,
          __top20: 0,
        });
      }

      const acc = map.get(info.key);
      acc.entries_count += 1;
      acc.__race_keys.add(getRaceKey(r));
      acc.races_count = acc.__race_keys.size;

      const dt = col.date ? String(r?.[col.date] ?? "") : "";
      if (!acc.__identity_date || !dt || dt >= acc.__identity_date) {
        // Driver aggregation keeps the most recent identity values.
        if (aggGroupBy === "driver") {
          const latestDriverName = cleanDriverDisplayName(
            col.driver ? r?.[col.driver] : acc.driver_name
          );
          if (latestDriverName) acc.driver_name = latestDriverName;

          // Use ONLY the most recent car/team identity for display.
          // Historical rows still contribute to the aggregates.
          if (col.car && r?.[col.car] != null && r?.[col.car] !== "") {
            acc.car = normalizeDisplayCarNumber(r?.[col.car]);
          }
          if (col.team && r?.[col.team]) acc.team = r?.[col.team];
        }

        // Car aggregation keeps the most recent team/manufacturer/sponsor for that car.
        if (aggGroupBy === "car") {
          if (col.team && r?.[col.team]) acc.team = r?.[col.team];
        }

        if (col.manufacturer && r?.[col.manufacturer]) acc.manufacturer = r?.[col.manufacturer];
        if (dt) acc.__identity_date = dt;
      }

      for (const f of sumFields) acc.__sum[f] += toNum(r?.[f]);

      for (const f of avgFields) {
        const raw = r?.[f];
        if (raw == null || raw === "") continue;
        acc.__sum[f] += toNum(raw);
        acc.__cnt[f] += 1;
      }

      if (bestField) {
        const raw = r?.[bestField];
        if (raw != null && raw !== "") acc.__best = Math.min(acc.__best, toNum(raw));
      }
      if (worstField) {
        const raw = r?.[worstField];
        if (raw != null && raw !== "") acc.__worst = Math.max(acc.__worst, toNum(raw));
      }

      const finish = col.finish ? r?.[col.finish] : "";
      const start = col.start ? r?.[col.start] : "";
      const fastest_lap = col.fl ? r?.[col.fl] : 0;
      const laps_led = col.ll ? r?.[col.ll] : 0;
      const laps = col.laps ? r?.[col.laps] : 0;

      // Completion percentage uses each entry's completed laps divided by the
      // total possible laps for that race. Summing both sides keeps the result
      // correct across different race lengths.
      if (col.laps) {
        const raceKey = getRaceKey(r);
        const possibleLaps = raceTotalLaps.get(raceKey) || 0;
        if (possibleLaps > 0) {
          acc.__laps_completed_sum += toNum(laps);
          acc.__possible_laps_sum += possibleLaps;
        }
      }

      if (col.finish && col.start) {
        acc.__dk_sum += calcDkPts({ finish, start, fastest_lap, laps_led });
        acc.__fd_sum += calcFdPts({ finish, start, laps, laps_led });
        acc.__pts_cnt += 1;

        const fin = Math.round(toNum(finish));
        if (fin === 1) acc.__win += 1;
        if (fin && fin <= 5) acc.__top5 += 1;
        if (fin && fin <= 10) acc.__top10 += 1;
        if (fin && fin <= 15) acc.__top15 += 1;
        if (fin && fin <= 20) acc.__top20 += 1;
      }
    }

    const out = [];

    for (const acc of map.values()) {
      const row = {
        ...(aggGroupBy === "driver" && acc.driver_name ? { driver_name: acc.driver_name } : {}),
        ...(aggGroupBy === "car" && acc.car != null ? { car: acc.car } : {}),
        ...(aggGroupBy === "team" && acc.team != null ? { team: acc.team } : {}),
        ...(aggGroupBy !== "team" && acc.team != null ? { team: acc.team } : {}),
        ...(aggGroupBy !== "car" && acc.car != null ? { car: acc.car } : {}),
        ...(acc.manufacturer != null ? { manufacturer: acc.manufacturer } : {}),
        ...(acc.series != null ? { series: acc.series } : {}),
        races_count: acc.races_count,
        entries_count: acc.entries_count,
      };

      for (const f of avgFields) {
        const cnt = acc.__cnt[f] || 0;
        row[f] = cnt ? acc.__sum[f] / cnt : "";
      }
      for (const f of sumFields) row[f] = acc.__sum[f];

      row.pct_laps_completed = acc.__possible_laps_sum > 0
        ? Math.round((acc.__laps_completed_sum / acc.__possible_laps_sum) * 10000) / 100
        : "";

      if (bestField) row[bestField] = acc.__best === Infinity ? "" : acc.__best;
      if (worstField) row[worstField] = acc.__worst === -Infinity ? "" : acc.__worst;

      row.dk_pts = acc.__pts_cnt ? acc.__dk_sum / acc.__pts_cnt : "";
      row.fd_pts = acc.__pts_cnt ? acc.__fd_sum / acc.__pts_cnt : "";

      row.wins = acc.__win;
      row.top_5 = acc.__top5;
      row.top_10 = acc.__top10;
      row.top_15 = acc.__top15;
      row.top_20 = acc.__top20;

      out.push(row);
    }

    // Raw GFS is omitted in Aggregate mode; GFS Rank is averaged.
    return out;
  }, [mode, baseFiltered, raceId, lastN, col, tracks, trackTypes, aggScope, aggSource, aggRaceIds, aggGroupBy]);

  // inject DK/FD points (race only). agg already has dk_pts/fd_pts averaged correctly.
  const rowsWithPoints = useMemo(() => {
    if (!modeRows.length) return modeRows;

    if (mode === "agg") {
      return buildPennRank(modeRows, mode, col);
    }

    const finishKey = col.finish;
    const startKey = col.start;
    const flKey = col.fl;
    const llKey = col.ll;
    const lapsKey = col.laps;

    let rowsOut = modeRows;

    if (finishKey && startKey && !Object.prototype.hasOwnProperty.call(modeRows?.[0] ?? {}, "dk_pts")) {
      rowsOut = modeRows.map((r) => {
        const finish = r?.[finishKey];
        const start = r?.[startKey];
        const fastest_lap = flKey ? r?.[flKey] : 0;
        const laps_led = llKey ? r?.[llKey] : 0;
        const laps = lapsKey ? r?.[lapsKey] : 0;

        const dk_pts = calcDkPts({ finish, start, fastest_lap, laps_led });
        const fd_pts = calcFdPts({ finish, start, laps, laps_led });

        return { ...r, dk_pts, fd_pts };
      });
    }

    return buildPennRank(rowsOut, mode, col);
  }, [modeRows, mode, col]);

  const allColumns = useMemo(() => {
    const cols = rowsWithPoints?.[0] ? Object.keys(rowsWithPoints[0]) : [];
    // hide race_id + driver_id globally
    const cleaned = cols.filter((c) => {
      const k = lower(String(c).trim());
      return k !== "race_id" && k !== "driver_id";
    });
    return dedupeCols(cleaned);
  }, [rowsWithPoints]);

// visible columns
const visibleColumns = useMemo(() => {
  if (!allColumns.length) return [];

  const lapsLedCol = col.ll || pickCol(allColumns, ["LapsLed", "laps_led"]);
  const fastLapCol = col.fl || pickCol(allColumns, ["FastestLaps", "fastest_lap", "fast_laps", "fastest_laps"]);
  const avgCol = col.avg || pickCol(allColumns, ["AvgRunningPos", "avg"]);
  const ratingCol = col.rating || pickCol(allColumns, ["DriverRating", "rating"]);
  const bestCol = col.best || pickCol(allColumns, ["BestPos", "best"]);
  const midCol = col.mid || pickCol(allColumns, ["MidRacePos", "mid"]);
  const worstCol = col.worst || pickCol(allColumns, ["WorstPos", "worst"]);

  // Metadata used by filters/race selection but intentionally hidden from the table.
  const hiddenTableKeys = new Set([
    "year",
    "raceid",
    "race_id",
    "racename",
    "race_name",
    "race",
    "driverid",
    "driver_id",
    "track",
    "track_name",
    "racedate",
    "race_date",
    "date",
    "series",
    "series_name",
  ]);

  const keepVisible = (c) => {
    const raw = lower(String(c).trim());
    const compact = raw.replace(/[\s._-]/g, "");

    // Series is intentionally shown in Aggregate mode,
    // immediately before Car / Team identity.
    if (mode === "agg" && (raw === "series" || compact === "series")) {
      return true;
    }

    return !hiddenTableKeys.has(raw) && !hiddenTableKeys.has(compact);
  };

  if (mode === "agg") {
    const groupIdentity =
      aggGroupBy === "car"
        ? ["series", "car", "team", "manufacturer"]
        : aggGroupBy === "team"
        ? ["series", "team", "manufacturer"]
        : ["series", "car", "driver_name", "team", "manufacturer"];

    const front = [
      ...groupIdentity,
      col.finish,
      col.start,
      lapsLedCol,
      fastLapCol,
      "dk_pts",
      "fd_pts",
      avgCol,
      ratingCol,
      "penn_rank",
      col.gfsRank,
      col.gfpd,
      "races_count",
      "wins",
      "top_5",
      "top_10",
      "top_15",
      "top_20",
      bestCol,
      midCol,
      worstCol,
    ].filter(Boolean);

    const set = new Set(front.map((x) => lower(String(x).trim())));
    const aggregateHidden = new Set([
      lower(String(col.gfs || "").trim()),
      lower(String(col.crewChief || "").trim()),
      lower(String(col.dnf || "").trim()),
      lower(String(col.cautions || "").trim()),
      lower(String(col.gfLaps || "").trim()),

      // Keep these metrics available in the data/Penn Rank calculations,
      // but do not display them in Aggregate mode.
      lower(String(col.gfp || "").trim()),
      lower(String(col.gftp || "").trim()),
      lower(String(col.qp || "").trim()),
      lower(String(col.t15 || "").trim()),

      "greenflagpasses",
      "green_flag_passes",
      "greenflagpassed",
      "green_flag_passed",
      "green_flag_times_passed",
      "qualitypasses",
      "quality_passes",
      "top15laps",
      "top_15_laps",

      "gfs",
      "crew_chief",
      "crewchief",
      "dnf",
      "cautions",
      "gf_laps",
      "gflaps",
      "entries_count",
      "track_type",
      "date_start",
      "date_end",
    ]);
    aggregateHidden.delete("");

    const tail = allColumns.filter((c) => {
      const key = lower(String(c).trim());
      return !set.has(key) && !aggregateHidden.has(key) && keepVisible(c);
    });
    return [...front, ...tail].filter((c) => !aggregateHidden.has(lower(String(c).trim())) && keepVisible(c));
  }

  // Single-race order:
  // Car, Driver, Team, Manufacturer, Sponsor, Crew Chief, Status, then race metrics.
  // Track / Race Date / Series and raw IDs/race metadata stay available to
  // filters but are intentionally not displayed in the table.
  const front = [
    col.car,
    col.driver,
    col.team,
    col.manufacturer,
    col.sponsor,
    col.crewChief,
    col.status,
    col.finish,
    col.start,
    lapsLedCol,
    fastLapCol,
    "dk_pts",
    "fd_pts",
    avgCol,
    col.gfs,
    col.gfsRank,
    ratingCol,
    "penn_rank",
    col.gfp,
    col.gfpd,
    col.gftp,
    col.qp,
    col.t15,
    col.gfLaps,
    col.cautions,
    col.dnf,
    bestCol,
    midCol,
    worstCol,
  ].filter(Boolean);

  const set = new Set(front.map((x) => lower(String(x).trim())));
  const tail = allColumns.filter((c) => !set.has(lower(String(c).trim())) && keepVisible(c));

  return [...front, ...tail].filter(keepVisible);
}, [allColumns, col, mode, aggGroupBy]);

const chartableColumns = useMemo(() => {
  return visibleColumns.filter((c) => {
    const k = lower(String(c).trim());
    return ![
      "driver_name",
      "driver",
      "series",
      "track_name",
      "track_type",
      "date",
      "date_start",
      "date_end",
      "race",
      "race_id",
      "status",
    ].includes(k);
  });
}, [visibleColumns]);

const exportColumns = useMemo(() => {
  if (!visibleColumns.length) return [];

  const removeSet = new Set([
    "series",
    "track_name",
    "track_type",
    "date_start",
    "date_end",
    "best",
    "mid",
    "worst",
  ]);

  // Also handle single-race raw column names from your source data
  const singleRaceRemoveSet = new Set([
    String(col.series || "").trim(),
    String(col.track || "").trim(),
    String(col.date || "").trim(),
    String(col.best || "").trim(),
    String(col.mid || "").trim(),
    String(col.worst || "").trim(),
  ]);

  return visibleColumns.filter((c) => {
    const key = String(c).trim();
    if (removeSet.has(key)) return false;
    if (singleRaceRemoveSet.has(key)) return false;
    return true;
  });
}, [visibleColumns, col]);

const searched = useMemo(() => {
  const needle = q.trim().toLowerCase();
  if (!needle) return rowsWithPoints;
  return rowsWithPoints.filter((r) =>
    visibleColumns.some((c) => String(r?.[c] ?? "").toLowerCase().includes(needle))
  );
}, [rowsWithPoints, visibleColumns, q]);

const sorted = useMemo(() => {
  const { key, dir } = sort;
  if (!key) return searched;
  const sign = dir === "asc" ? 1 : -1;
  return [...searched].sort((a, b) => {
    const av = a?.[key];
    const bv = b?.[key];
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * sign;
    const an = Number(av);
    const bn = Number(bv);
    if (!Number.isNaN(an) && !Number.isNaN(bn)) return (an - bn) * sign;
    return String(av ?? "").localeCompare(String(bv ?? "")) * sign;
  });
}, [searched, sort]);

// Raw races currently included by the Aggregate controls.
// This intentionally mirrors the aggregate race-picking rules so the
// "Filtered Race Log" matches the races that produced the aggregate row.
const aggSelectedRawRows = useMemo(() => {
  if (mode !== "agg") return [];
  if (aggSource === "races" && !(aggRaceIds && aggRaceIds.length)) return [];

  const getRaceKey = (r) => {
    if (col.race_id) return String(r?.[col.race_id] ?? "");
    const d = col.date ? String(r?.[col.date] ?? "") : "";
    const s = col.series ? publicSeriesLabel(r?.[col.series]) : "";
    const t = col.track ? String(r?.[col.track] ?? "") : "";
    const rn = col.race ? String(r?.[col.race] ?? "") : "";
    return [s, d, t, rn].filter(Boolean).join("|");
  };

  const dateDescLocal = (a, b) => {
    const ad = col.date ? String(a?.[col.date] ?? "") : "";
    const bd = col.date ? String(b?.[col.date] ?? "") : "";
    return bd.localeCompare(ad);
  };

  const ordered = [...baseFiltered].sort(dateDescLocal);
  let pickedRaceKeys = [];

  if (aggSource === "races") {
    pickedRaceKeys = (aggRaceIds || []).filter(Boolean);
  } else if (aggScope === "per_track" && tracks.length && col.track) {
    const perTrack = new Map();
    const seenByTrack = new Map();
    for (const t of tracks) {
      perTrack.set(t, []);
      seenByTrack.set(t, new Set());
    }

    for (const r of ordered) {
      const tval = String(r?.[col.track] ?? "");
      if (!perTrack.has(tval)) continue;
      const key = getRaceKey(r);
      if (!key) continue;
      const seen = seenByTrack.get(tval);
      if (seen.has(key)) continue;
      seen.add(key);
      perTrack.get(tval).push(key);

      let done = true;
      for (const arr of perTrack.values()) {
        if (arr.length < Number(lastN || 0)) {
          done = false;
          break;
        }
      }
      if (done) break;
    }

    pickedRaceKeys = Array.from(new Set(Array.from(perTrack.values()).flat()));
  } else {
    const seenRaces = new Set();
    for (const r of ordered) {
      const key = getRaceKey(r);
      if (!key || seenRaces.has(key)) continue;
      seenRaces.add(key);
      pickedRaceKeys.push(key);
      if (pickedRaceKeys.length >= Number(lastN || 0)) break;
    }
  }

  return baseFiltered.filter((r) => pickedRaceKeys.includes(getRaceKey(r)));
}, [mode, baseFiltered, aggSource, aggRaceIds, aggScope, tracks, lastN, col]);

function openRaceLog(row) {
  if (mode !== "agg") return;
  if (aggGroupBy !== "driver" && aggGroupBy !== "car") return;

  const seriesName = String(row?.series ?? row?.[col.series] ?? "").trim();

  if (aggGroupBy === "car") {
    const carNumber = normalizeDisplayCarNumber(row?.car ?? row?.[col.car]);
    if (!carNumber) return;
    setRaceLogTarget({ type: "car", value: carNumber, series: seriesName, label: `Car #${carNumber}` });
  } else {
    const driverName = cleanDriverDisplayName(String(row?.driver_name ?? row?.[col.driver] ?? "").trim());
    if (!driverName) return;
    setRaceLogTarget({
      type: "driver",
      value: canonicalDriverKey(driverName, { stripSuffix: false }),
      series: seriesName,
      label: driverName,
    });
  }

  setRaceLogScope("filtered");
}

const raceTotalLapsByKey = useMemo(() => {
  const totals = new Map();

  const getRaceKey = (r) => {
    if (col.race_id) return String(r?.[col.race_id] ?? "");
    const d = col.date ? String(r?.[col.date] ?? "") : "";
    const s = col.series ? String(r?.[col.series] ?? "") : "";
    const t = col.track ? String(r?.[col.track] ?? "") : "";
    const rn = col.race ? String(r?.[col.race] ?? "") : "";
    return [s, d, t, rn].filter(Boolean).join("|");
  };

  for (const r of rows) {
    const key = getRaceKey(r);
    if (!key) continue;
    const completed = col.laps ? toNum(r?.[col.laps]) : 0;
    if (!completed) continue;
    totals.set(key, Math.max(totals.get(key) || 0, completed));
  }

  return { totals, getRaceKey };
}, [rows, col]);

const raceLogRows = useMemo(() => {
  if (!raceLogTarget) return [];

  const source = raceLogScope === "full" ? rows : aggSelectedRawRows;

  const matched = source.filter((r) => {
    const rowSeries = col.series ? String(r?.[col.series] ?? "").trim() : "";
    if (raceLogTarget.series && rowSeries !== raceLogTarget.series) return false;

    if (raceLogTarget.type === "car") {
      return normalizeDisplayCarNumber(col.car ? r?.[col.car] : "") === raceLogTarget.value;
    }

    const driverName = cleanDriverDisplayName(col.driver ? r?.[col.driver] : "");
    return canonicalDriverKey(driverName, { stripSuffix: false }) === raceLogTarget.value;
  });

  // IMPORTANT: chronological = earliest -> latest.
  matched.sort((a, b) => {
    const ad = col.date ? String(a?.[col.date] ?? "") : "";
    const bd = col.date ? String(b?.[col.date] ?? "") : "";
    return ad.localeCompare(bd);
  });

  return matched.map((r) => {
    const finish = col.finish ? r?.[col.finish] : "";
    const start = col.start ? r?.[col.start] : "";
    const fastest_lap = col.fl ? r?.[col.fl] : 0;
    const laps_led = col.ll ? r?.[col.ll] : 0;
    const laps = col.laps ? r?.[col.laps] : 0;

    return {
      race_date: col.date ? r?.[col.date] : "",
      series: col.series ? publicSeriesLabel(r?.[col.series]) : "",
      track: col.track ? r?.[col.track] : "",
      race: col.race ? r?.[col.race] : "",
      car: col.car ? normalizeDisplayCarNumber(r?.[col.car]) : "",
      driver: col.driver ? cleanDriverDisplayName(r?.[col.driver]) : "",
      start: start,
      finish: finish,
      avg_run: col.avg ? r?.[col.avg] : "",
      gfs: col.gfs ? r?.[col.gfs] : "",
      gfs_rank: col.gfsRank ? r?.[col.gfsRank] : "",
      driver_rating: col.rating ? r?.[col.rating] : "",
      laps_led: col.ll ? r?.[col.ll] : "",
      fastest_laps: col.fl ? r?.[col.fl] : "",
      pct_laps_completed: (() => {
        const raceKey = raceTotalLapsByKey.getRaceKey(r);
        const totalLaps = raceTotalLapsByKey.totals.get(raceKey) || 0;
        const completedLaps = col.laps ? toNum(r?.[col.laps]) : 0;
        return totalLaps > 0 ? (completedLaps / totalLaps) * 100 : "";
      })(),
      dk_pts: col.finish && col.start ? calcDkPts({ finish, start, fastest_lap, laps_led }) : "",
      fd_pts: col.finish && col.start ? calcFdPts({ finish, start, laps, laps_led }) : "",
      status: col.status ? r?.[col.status] : "",
    };
  });
}, [raceLogTarget, raceLogScope, rows, aggSelectedRawRows, col, raceTotalLapsByKey]);

const raceLogColumns = [
  "race_date",
  "series",
  "track",
  "race",
  "car",
  "driver",
  "start",
  "finish",
  "avg_run",
  "gfs",
  "gfs_rank",
  "driver_rating",
  "laps_led",
  "fastest_laps",
  "pct_laps_completed",
  "dk_pts",
  "fd_pts",
  "status",
];

const activeDriverMap = useMemo(() => {
  const map = new Map();

  for (const r of activeRows || []) {
    const driver = String(
      r?.Driver ?? r?.Name ?? r?.["Driver Name"] ?? ""
    ).trim();

    if (!driver) continue;

    const rawCarNum =
      r?.["Car #"] ??
      r?.["Car#"] ??
      r?.["Number"] ??
      r?.["#"] ??
      null;

    const n = Number(rawCarNum);
    if (!Number.isFinite(n)) continue;

    const payload = { carNum: Math.round(n), rawRow: r };

    map.set(canonicalDriverKey(driver, { stripSuffix: false }), payload);
    map.set(canonicalDriverKey(driver, { stripSuffix: true }), payload);
  }

  return map;
}, [activeRows]);

const activeTeamMap = useMemo(() => {
  const map = new Map();

  for (const r of activeRows || []) {
    const rawTeam = String(
      r?.Team ??
      r?.["Team Name"] ??
      r?.TeamName ??
      r?.team ??
      r?.team_name ??
      r?.Organization ??
      ""
    ).trim();

    if (!rawTeam) continue;

    const canonical = canonicalTeamName(rawTeam);
    // Always display the modern/canonical organization name in team charts.
    // Example: Motorsports Business Management / MBM -> Garage 66.
    const currentTeamName = canonical;
    const teamKey = normalizeTeamLogoKey(canonical);
    if (!teamKey) continue;

    const bucket = String(r?.__activeSeriesBucket || seriesBucket(r?.Series ?? r?.series ?? "cup"));
    const payload = { teamName: currentTeamName, canonicalTeamName: canonical, rawRow: r, bucket };

    // Series-specific key is preferred. Generic key is useful when the selected
    // active file does not carry a series field internally.
    map.set(`${bucket}|${teamKey}`, payload);
    if (!map.has(teamKey)) map.set(teamKey, payload);
  }

  return map;
}, [activeRows]);

function onHeaderClick(colName) {
  setSort((p) =>
    p.key === colName
      ? { key: colName, dir: p.dir === "asc" ? "desc" : "asc" }
      : { key: colName, dir: "asc" }
  );
}

useEffect(() => {
  if (!rowsWithPoints.length) return;
  if (col.finish) setSort((p) => (p.key ? p : { key: col.finish, dir: "asc" }));
  else setSort((p) => (p.key ? p : { key: "dk_pts", dir: "desc" }));
}, [rowsWithPoints.length, col.finish]);

useEffect(() => {
  if (!chartableColumns.length) return;

  const defX =
    chartableColumns.find((c) => lower(c) === lower(col.avg || "")) ||
    chartableColumns.find((c) => lower(c) === lower(col.finish || "")) ||
    chartableColumns.find((c) => lower(c) === "dk_pts") ||
    chartableColumns[0] ||
    "";

  const defY =
    chartableColumns.find((c) => lower(c) === lower(col.rating || "")) ||
    chartableColumns.find((c) => lower(c) === "penn_rank") ||
    chartableColumns.find((c) => lower(c) === "fd_pts") ||
    chartableColumns[Math.min(1, chartableColumns.length - 1)] ||
    "";

  setXCol(defX);
  setYCol(defY);
}, [chartableColumns, col.avg, col.finish, col.rating]);

// If track type changes, clear any selected tracks that no longer match (agg mode)
useEffect(() => {
  if (mode !== "agg") return;
  if (!trackTypes.length) return;
  if (!tracks.length) return;
  const keep = tracks.filter((t) => matchesTrackTypes(t, trackTypes));
  if (keep.length !== tracks.length) setTracks(keep);
}, [trackTypes, mode]); // eslint-disable-line react-hooks/exhaustive-deps

const autoRevX = useMemo(() => dirForCol(xCol) === "lower", [xCol]);
const autoRevY = useMemo(() => dirForCol(yCol) === "lower", [yCol]);

const isRevX = revX == null ? autoRevX : revX;
const isRevY = revY == null ? autoRevY : revY;

const chartPoints = useMemo(() => {
  if (!xCol || !sorted.length) return [];

  const pts = [];

  for (const r of sorted) {
    const xv = parseNumericLike(r?.[xCol]);
    const yv = yCol ? parseNumericLike(r?.[yCol]) : null;
    if (xv == null) continue;
    if (yCol && yv == null) continue;

    let label = "";
    let pointKey = "";
    let carNum = null;
    let useLogo = false;
    let teamName = "";
    let useTeamLogo = false;

    if (mode === "agg" && aggGroupBy === "car") {
      const car = String(r?.car ?? r?.[col.car] ?? "").trim();
      if (!car) continue;
      label = `Car ${car}`;
      pointKey = `car-${r?.series ?? ""}-${car}`;
      const n = Number(car);
      if (Number.isFinite(n)) {
        carNum = Math.round(n);
        useLogo = true;
      }
    } else if (mode === "agg" && aggGroupBy === "team") {
      const historicalTeam = canonicalTeamName(String(r?.team ?? r?.[col.team] ?? "").trim());
      if (!historicalTeam) continue;

      const rowSeries = String(r?.series ?? r?.[col.series] ?? series ?? "cup");
      const bucket = seriesBucket(rowSeries);
      const teamKey = normalizeTeamLogoKey(historicalTeam);
      const active =
        activeTeamMap.get(`${bucket}|${teamKey}`) ||
        activeTeamMap.get(teamKey);

      // Team charts only show organizations present in the CURRENT entry-list
      // data.json. The displayed name/logo also comes from that current file,
      // so merged/rebranded historical names never create duplicate points.
      if (!active) continue;

      label = active.teamName;
      teamName = active.teamName;
      useTeamLogo = true;
      pointKey = `team-${bucket}-${teamKey}`;
    } else {
      const driver = String(r?.driver_name ?? r?.[col.driver] ?? "").trim();
      if (!driver) continue;
      label = driver;
      pointKey = canonicalDriverKey(driver, { stripSuffix: false });

      const active =
        activeDriverMap.get(canonicalDriverKey(driver, { stripSuffix: false })) ||
        activeDriverMap.get(canonicalDriverKey(driver, { stripSuffix: true }));

      // Driver charts should only include drivers in the CURRENT field for
      // the selected series. activeRows comes from that series' latest data.json.
      // This prevents historical/inactive drivers from appearing as initials.
      if (!active) continue;

      if (active?.carNum) {
        carNum = active.carNum;
        useLogo = true;
      } else {
        continue;
      }
    }

    pts.push({
      driver: label,
      driverKey: pointKey,
      xVal: xv,
      yVal: yv,
      carNum,
      useLogo,
      teamName,
      useTeamLogo,
      rawRow: r,
    });
  }

  return pts;
}, [sorted, xCol, yCol, col.driver, col.car, col.team, col.series, activeDriverMap, activeTeamMap, mode, aggGroupBy, series]);

const chartStats = useMemo(() => {
  if (!yCol || !chartPoints.length) return null;

  let xmin = Infinity;
  let xmax = -Infinity;
  let ymin = Infinity;
  let ymax = -Infinity;
  let sx = 0;
  let sy = 0;

  for (const p of chartPoints) {
    xmin = Math.min(xmin, p.xVal);
    xmax = Math.max(xmax, p.xVal);
    ymin = Math.min(ymin, p.yVal);
    ymax = Math.max(ymax, p.yVal);
    sx += p.xVal;
    sy += p.yVal;
  }

  const n = chartPoints.length;

  return {
    xmin,
    xmax,
    ymin,
    ymax,
    xAvg: sx / n,
    yAvg: sy / n,
  };
}, [chartPoints, yCol]);

const barPlot = useMemo(() => {
  if (yCol || !xCol || !chartPoints.length) return null;

  const direction = dirForCol(xCol);
  const ranked = [...chartPoints].sort((a, b) => {
    if (direction === "lower") return a.xVal - b.xVal;
    return b.xVal - a.xVal;
  });

  // Bar charts can be trimmed to the best N after ranking.
  // barLimit === 0 means show the full ranked field.
  const ordered = barLimit > 0 ? ranked.slice(0, barLimit) : ranked;

  const W = 980;
  const rowH = 48;
  const pad = { l: 232, r: 92, t: 58, b: 54 };
  const innerW = W - pad.l - pad.r;
  const H = Math.max(300, pad.t + pad.b + ordered.length * rowH);

  const values = ordered.map((p) => p.xVal).filter(Number.isFinite);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const dataSpread = dataMax - dataMin;

  // Bar-chart autoscaling:
  // Do NOT force zero into the domain when every value is clustered far from zero.
  // Example: GFS 148-152 now zooms to roughly that range instead of 0-152.
  // Mixed positive/negative stats still keep zero as the true baseline.
  let min;
  let max;
  let baselineValue;

  if (dataSpread === 0) {
    const padAmt = Math.max(Math.abs(dataMax) * 0.04, 0.5);
    min = dataMin - padAmt;
    max = dataMax + padAmt;

    if (dataMin > 0) baselineValue = min;
    else if (dataMax < 0) baselineValue = max;
    else baselineValue = 0;
  } else if (dataMin >= 0) {
    // All-positive values: zoom around the observed range.
    // Give the low end a little more breathing room so the shortest bar
    // remains visible while differences between drivers are easy to see.
    const leftPad = Math.max(dataSpread * 0.22, Math.abs(dataMax) * 0.004, 0.25);
    const rightPad = Math.max(dataSpread * 0.08, Math.abs(dataMax) * 0.002, 0.12);
    min = Math.max(0, dataMin - leftPad);
    max = dataMax + rightPad;
    baselineValue = min;
  } else if (dataMax <= 0) {
    // All-negative values: mirror the positive behavior.
    const leftPad = Math.max(dataSpread * 0.08, Math.abs(dataMin) * 0.002, 0.12);
    const rightPad = Math.max(dataSpread * 0.22, Math.abs(dataMin) * 0.004, 0.25);
    min = dataMin - leftPad;
    max = Math.min(0, dataMax + rightPad);
    baselineValue = max;
  } else {
    // Values cross zero: zero must remain the real baseline.
    const outerPad = Math.max(dataSpread * 0.06, 0.12);
    min = dataMin - outerPad;
    max = dataMax + outerPad;
    baselineValue = 0;
  }

  if (!(max > min)) {
    max = min + 1;
  }

  const range = max - min || 1;
  const sx = (v) => pad.l + ((v - min) / range) * innerW;
  const baselineX = sx(baselineValue);

  const niceStep = (rawStep) => {
    if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
    const power = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const fraction = rawStep / power;
    const niceFraction =
      fraction <= 1 ? 1 :
      fraction <= 2 ? 2 :
      fraction <= 2.5 ? 2.5 :
      fraction <= 5 ? 5 : 10;
    return niceFraction * power;
  };

  const makeTicks = (lo, hi, targetCount = 7) => {
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [];
    if (lo === hi) return [lo];

    const step = niceStep((hi - lo) / Math.max(1, targetCount - 1));
    const first = Math.ceil(lo / step) * step;
    const last = Math.floor(hi / step) * step;
    const ticks = [];

    for (let v = first, guard = 0; v <= last + step * 1e-9 && guard < 30; v += step, guard++) {
      ticks.push(Math.round(v * 1e10) / 1e10);
    }

    // Only force zero as a tick when the visible domain actually crosses zero.
    if (lo < 0 && hi > 0 && !ticks.some((v) => Math.abs(v) < 1e-10)) ticks.push(0);

    return ticks.sort((a, b) => a - b);
  };

  // Keep one decimal for bar labels and ticks, regardless of chart range.
  const formatValue = (value) => Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  const ticks = makeTicks(min, max).map((value) => ({
    value,
    x: sx(value),
    label: formatValue(value),
  }));

  const rows = ordered.map((p, index) => {
    const centerY = pad.t + index * rowH + rowH / 2;
    const valueX = sx(p.xVal);
    const rawWidth = valueX - baselineX;

    return {
      ...p,
      id: p.driverKey || `${p.driver}-${index}`,
      centerY,
      barY: centerY - 11,
      barH: 22,
      zeroX: baselineX, // kept as zeroX so the existing SVG renderer needs no other changes
      baselineValue,
      valueX,
      barWidth: rawWidth,
      valueLabel: formatValue(p.xVal),
    };
  });

  return {
    W,
    H,
    pad,
    innerW,
    rowH,
    min,
    max,
    dataMin,
    dataMax,
    baselineValue,
    zeroX: baselineX,
    ticks,
    rows,
  };
}, [chartPoints, xCol, yCol, barLimit]);

const plot = useMemo(() => {
  if (!chartStats || !chartPoints.length) return null;

  const W = 980;
  const H = 540;
  // Extra room for numeric tick labels without adding chart grid lines.
  const pad = { l: 72, r: 24, t: 22, b: 72 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const x0 = chartStats.xmin;
  const x1 = chartStats.xmax;
  const y0 = chartStats.ymin;
  const y1 = chartStats.ymax;

  const sx = (v) => {
    const t = (v - x0) / (x1 - x0 || 1);
    const u = isRevX ? 1 - t : t;
    return pad.l + u * innerW;
  };

  const sy = (v) => {
    const t = (v - y0) / (y1 - y0 || 1);
    const u = isRevY ? t : 1 - t;
    return pad.t + u * innerH;
  };

  const niceStep = (rawStep) => {
    if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
    const power = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const fraction = rawStep / power;
    const niceFraction =
      fraction <= 1 ? 1 :
      fraction <= 2 ? 2 :
      fraction <= 2.5 ? 2.5 :
      fraction <= 5 ? 5 : 10;
    return niceFraction * power;
  };

  const makeTicks = (min, max, targetCount = 6) => {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
    if (min === max) return [min];

    const step = niceStep((max - min) / Math.max(1, targetCount - 1));
    const first = Math.ceil(min / step) * step;
    const last = Math.floor(max / step) * step;
    const ticks = [];

    for (let v = first, guard = 0; v <= last + step * 1e-9 && guard < 20; v += step, guard++) {
      ticks.push(Math.round(v * 1e10) / 1e10);
    }

    if (ticks.length < 3) {
      return Array.from({ length: 5 }, (_, i) => {
        const v = min + ((max - min) * i) / 4;
        return Math.round(v * 1e10) / 1e10;
      });
    }

    return ticks;
  };

  const formatTick = (value, min, max) => {
    const range = Math.abs(max - min);
    let decimals = 0;
    if (range < 1) decimals = 2;
    else if (range < 10) decimals = 1;
    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  };

  const xTicks = makeTicks(x0, x1).map((value) => ({
    value,
    x: sx(value),
    label: formatTick(value, x0, x1),
  }));

  const yTicks = makeTicks(y0, y1).map((value) => ({
    value,
    y: sy(value),
    label: formatTick(value, y0, y1),
  }));

  const base = chartPoints.map((p) => ({
    id: p.driverKey || String(p.carNum ?? ""),
    x: sx(p.xVal),
    y: sy(p.yVal),
    ...p,
  }));

  // Team logos are intentionally larger and are NOT drawn inside bubbles.
  // Give them more collision room so the actual logo artwork stays readable.
  const pointRadius = mode === "agg" && aggGroupBy === "team" ? 34 : 16;

  const spread = spreadPoints(base, { r: pointRadius, iters: 16, pad: 4 }).map((p) => ({
    ...p,
    x: clamp(p.x, pad.l + pointRadius, pad.l + innerW - pointRadius),
    y: clamp(p.y, pad.t + pointRadius, pad.t + innerH - pointRadius),
  }));

  const avgX = sx(chartStats.xAvg);
  const avgY = sy(chartStats.yAvg);

  return { W, H, pad, innerW, innerH, points: spread, avgX, avgY, xTicks, yTicks };
}, [chartStats, chartPoints, isRevX, isRevY, mode, aggGroupBy]);

function resetAll() {
  setSeries("All");
  setTrack("All");
  setTracks([]);
  setTrackTypes([]);
  setYears([]);
  setRaceId("");
  setLastN(0);
  setAggScope("global");
  setAggSource("recent");
  setAggRaceIds([]);
  setAggGroupBy("driver");
  setExcludeExhibition(true);
  setQ("");
  setSort({ key: "", dir: "asc" });
  setView("table");
  setRevX(null);
  setRevY(null);
  setChartTheme("dark");
  setBarLimit(10);
  setRaceLogTarget(null);
  setRaceLogScope("filtered");
}

  const title = useMemo(() => {
    if (mode === "race") return "NASCAR Race Analyzer — Race View";
    return "NASCAR Race Analyzer — Aggregated View";
  }, [mode]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-5 md:px-6">
      <div className="w-full space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">{title}</h1>
            <span className="text-xs font-normal italic text-slate-100">Updated: {updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</span>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            className="px-3 py-2 rounded-md bg-slate-900/70 text-slate-100 text-xs font-semibold hover:bg-slate-800 transition border border-slate-700 shadow-sm"
            onClick={resetAll}
            title="Reset all filters and options"
          >
            Reset
          </button>          </div>
        </header>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/95 px-4 py-3 shadow-sm">
            <div className="inline-flex flex-wrap items-center gap-1 border-b border-slate-800">
              <button
                type="button"
                onClick={() => setView("table")}
                className={`inline-flex h-9 items-center border-b-2 px-3 text-xs font-semibold transition ${
                  view === "table" ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"
                }`}
              >
                Table
              </button>
              <button
                type="button"
                onClick={() => setView("charts")}
                className={`inline-flex h-9 items-center border-b-2 px-3 text-xs font-semibold transition ${
                  view === "charts" ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"
                }`}
              >
                Charts
              </button>
            </div>        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-800 bg-slate-900/95 p-4 shadow-sm">
          <div className="flex flex-col">
            <label className="text-[11px] text-slate-400 mb-1">Mode</label>
            <select
              className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={mode}
              onChange={(e) => {
                const next = e.target.value;
                setMode(next);
                if (next === "race") {
                  setTracks([]);
                  setAggRaceIds([]);
                  setAggSource("recent");
                  setTrackTypes([]); // track type is agg-only
                }
                if (next === "agg") {
                  setTrack("All");
                }
                setQ("");
                setSort({ key: "", dir: "asc" });
              }}
            >
              {MODES.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-[11px] text-slate-400 mb-1">Series</label>
            <select
              className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={series}
              onChange={(e) => setSeries(e.target.value)}
            >
              {seriesOptions.map((s) => (
                <option key={s} value={s}>
                  {publicSeriesLabel(s)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-[11px] text-slate-400 mb-1">Race Filters</label>
            <label className="inline-flex items-center gap-2 border border-slate-700 rounded-md px-3 py-2 bg-slate-900/80 text-xs text-slate-100 h-[38px]">
              <input
                type="checkbox"
                checked={excludeExhibition}
                onChange={(e) => setExcludeExhibition(e.target.checked)}
              />
              <span>Exclude Exhibition Races</span>
            </label>
          </div>

          <div className="flex flex-col">
            <label className="text-[11px] text-slate-400 mb-1">Track</label>
            {mode === "agg" ? (
              <MultiTrackDropdown options={trackOptions.filter((t) => t !== "All")} values={tracks} onChange={setTracks} />
            ) : (
              <select
                className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                value={track}
                onChange={(e) => setTrack(e.target.value)}
              >
                {trackOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            )}
          </div>

          {mode === "agg" ? (
            <div className="flex flex-col">
              <label className="text-[11px] text-slate-400 mb-1">Track Type</label>
              <div className="flex items-center gap-2">
                <MultiTrackTypeDropdown
                  options={trackTypeOptions}
                  values={trackTypes}
                  onChange={setTrackTypes}
                />
                <TrackTypeHelpDropdown />
              </div>
            </div>
          ) : null}

          <div className="flex flex-col">
            <label className="text-[11px] text-slate-400 mb-1">Year</label>
            <MultiYearDropdown options={yearOptions.filter((y) => y !== "All")} values={years} onChange={setYears} />
          </div>

          {mode === "race" && (
            <div className="flex flex-col min-w-[22rem]">
              <label className="text-[11px] text-slate-400 mb-1">Race</label>
              <select
                className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                value={raceId}
                onChange={(e) => setRaceId(e.target.value)}
              >
                {raceOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === "agg" && (
            <>
              <div className="flex flex-col">
                <label className="text-[11px] text-slate-400 mb-1">Aggregate By</label>
                <select
                  className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={aggGroupBy}
                  onChange={(e) => setAggGroupBy(e.target.value)}
                >
                  <option value="driver">Driver</option>
                  <option value="car">Car Number</option>
                  <option value="team">Team</option>
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-[11px] text-slate-400 mb-1">Races included</label>
                <select
                  className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={aggSource}
                  onChange={(e) => {
                    const next = e.target.value;
                    setAggSource(next);
                    if (next === "recent") setAggRaceIds([]);
                  }}
                >
                  <option value="recent">Recent races</option>
                  <option value="races">Pick races</option>
                </select>
              </div>

              {aggSource === "recent" ? (
                <>
                  <div className="flex flex-col">
                    <label className="text-[11px] text-slate-400 mb-1">How many races (N)</label>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      className="border border-slate-700 rounded-md px-2 py-2 text-xs w-44 bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={lastN}
                      onChange={(e) => setLastN(Number(e.target.value))}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-[11px] text-slate-400 mb-1">Apply N</label>
                    <select
                      className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={aggScope}
                      onChange={(e) => setAggScope(e.target.value)}
                      title="Global = most recent N overall; Per track = most recent N for each selected track"
                    >
                      <option value="global">Overall (across all selected)</option>
                      <option value="per_track">Per track (N at each track)</option>
                    </select>
                  </div>
                </>
              ) : (
                <div className="flex flex-col min-w-full sm:w-[30rem]">
                  <label className="text-[11px] text-slate-400 mb-1">Races (multi-select)</label>
                  <MultiRaceDropdown options={aggRaceOptions} values={aggRaceIds} onChange={setAggRaceIds} />
                </div>
              )}
            </>
          )}

          <div className="flex flex-col w-72 md:w-full sm:w-[26rem]">
            <label className="text-[11px] text-slate-400 mb-1">Search</label>
            <input
              className="border border-slate-700 rounded-md px-3 py-2 text-xs bg-slate-900/70 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              placeholder="Search any column…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>



          <button
            className="px-3 py-2 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400 transition border border-sky-400 shadow-sm"
            onClick={() => exportToCsv(`NASCAR_DataExplorer_${mode}.csv`, sorted, exportColumns)}
            disabled={!sorted.length}
            title={!sorted.length ? "No rows to export" : "Export current view"}
          >
            Export CSV
          </button>


        </div>

        {err && <div className="text-sm text-rose-400">{err}</div>}

        {view === "table" ? (
          <>
            {/* Table */}
            <div className="border border-slate-800 rounded-2xl shadow-sm overflow-auto max-h-[72vh] bg-slate-900/95">
              <table className="min-w-max text-xs leading-tight w-full">
                <thead className="bg-slate-900 sticky top-0 z-30">
                  <tr>
                    {visibleColumns.map((c) => {
                      const identitySticky = isPrimaryStickyCol(c, col, mode, aggGroupBy);
                      const isDriverColumn = isDriverCol(c, col);
                      return (
                        <th
                          key={c}
                          className={
                            "px-2 py-2 font-semibold whitespace-nowrap cursor-pointer select-none text-slate-200 " +
                            (identitySticky
                              ? `sticky left-0 z-40 bg-slate-900 pl-3 pr-4 ${isDriverColumn ? "text-center" : "text-left"}`
                              : "text-center")
                          }
                          onClick={() => onHeaderClick(c)}
                          title="Click to sort"
                        >
                          <div
                            className={
                              "inline-flex items-center gap-1 " +
                              (identitySticky && !isDriverColumn ? "justify-start" : "justify-center")
                            }
                          >
                            <span>{prettyHeader(c)}</span>
                            {sort.key === c ? (
                              <span className="text-[9px] opacity-60">{sort.dir === "asc" ? "▲" : "▼"}</span>
                            ) : null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                        Loading…
                      </td>
                    </tr>
                  ) : sorted.length === 0 ? (
                    <tr>
                      <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                        No rows.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((r, i) => {
                      const rowBg = i % 2 ? "bg-slate-900" : "bg-slate-950";
                      const driverBgSolid = i % 2 ? "bg-slate-900" : "bg-slate-950";
                      return (
                        <tr key={i} className={rowBg}>
                          {visibleColumns.map((c) => {
                            const identitySticky = isPrimaryStickyCol(c, col, mode, aggGroupBy);
                            const isDriverColumn = isDriverCol(c, col);
                            return (
                              <td
                                key={c}
                                className={
                                  "px-2 py-1 whitespace-nowrap text-slate-100 " +
                                  (identitySticky
                                    ? `sticky left-0 z-20 ${driverBgSolid} pl-3 pr-4 ${isDriverColumn ? "text-center" : "text-left"}`
                                    : "text-center")
                                }
                              >
                                {(() => {
                                  const value =
                                    c === col.gfs && r?.[c] != null && r?.[c] !== ""
                                      ? `${fmt(r?.[c])} mph`
                                      : c === "pct_laps_completed" && r?.[c] != null && r?.[c] !== ""
                                      ? `${Number(r?.[c]).toFixed(1)}%`
                                      : c === col.date
                                      ? formatEasternRaceDate(r?.[c])
                                      : c === col.series
                                      ? publicSeriesLabel(r?.[c])
                                      : fmt(r?.[c]);

                                  const isRaceLogLink =
                                    mode === "agg" &&
                                    ((aggGroupBy === "driver" && (c === "driver_name" || isDriverColumn)) ||
                                      (aggGroupBy === "car" && lower(String(c).trim()) === "car"));

                                  return isRaceLogLink ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openRaceLog(r);
                                      }}
                                      className="font-semibold text-sky-300 hover:text-sky-200 hover:underline underline-offset-2"
                                      title="Open chronological race log"
                                    >
                                      {value}
                                    </button>
                                  ) : (
                                    value
                                  );
                                })()}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="text-[11px] text-slate-400">
              Tip: This view summarizes the selected races for each driver. DK/FD Pts and Avg Finish are averages, while Wins and Top 5/10/15/20 are raw finish counts. Best, Mid, and Worst Finish show the driver’s range of outcomes.
            </div>
          </>
        ) : (
          <>
            <div className="border border-slate-800 rounded-2xl shadow-sm bg-slate-900/95 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-col">
                    <label className="text-[11px] text-slate-400 mb-1">Stat 1</label>
                    <select
                      value={xCol}
                      onChange={(e) => setXCol(e.target.value)}
                      className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    >
                      {chartableColumns.map((c) => (
                        <option key={c} value={c}>
                          {prettyHeader(c)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-[11px] text-slate-400 mb-1">Stat 2 (optional)</label>
                    <select
                      value={yCol}
                      onChange={(e) => setYCol(e.target.value)}
                      className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    >
                      <option value="">None — Bar Chart</option>
                      {chartableColumns.map((c) => (
                        <option key={c} value={c}>
                          {prettyHeader(c)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {yCol ? (
                    <>
                      <label className="inline-flex items-center gap-2 text-xs text-slate-300">
                        <input
                          type="checkbox"
                          checked={isRevX}
                          onChange={(e) => setRevX(e.target.checked)}
                        />
                        Reverse X
                      </label>

                      <label className="inline-flex items-center gap-2 text-xs text-slate-300">
                        <input
                          type="checkbox"
                          checked={isRevY}
                          onChange={(e) => setRevY(e.target.checked)}
                        />
                        Reverse Y
                      </label>
                    </>
                  ) : null}

                  {!yCol ? (
                    <div className="flex flex-col">
                      <label className="text-[11px] text-slate-400 mb-1">Show</label>
                      <select
                        value={barLimit}
                        onChange={(e) => setBarLimit(Number(e.target.value))}
                        className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      >
                        <option value={5}>Top 5</option>
                        <option value={10}>Top 10</option>
                        <option value={15}>Top 15</option>
                        <option value={20}>Top 20</option>
                        <option value={0}>All</option>
                      </select>
                    </div>
                  ) : null}

                  <div className="flex flex-col">
                    <label className="text-[11px] text-slate-400 mb-1">Chart Background</label>
                    <select
                      value={chartTheme}
                      onChange={(e) => setChartTheme(e.target.value)}
                      className="border border-slate-700 rounded-md px-2 py-2 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    >
                      <option value="dark">Dark</option>
                      <option value="light">White</option>
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSavePdf}
                  disabled={savingPdf || (!yCol ? !barPlot : !plot || !chartStats)}
                  aria-busy={savingPdf}
                  title="Save the complete chart on one PDF page"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-sky-500/50 bg-sky-500/10 px-4 py-2 text-xs font-semibold text-sky-200 hover:bg-sky-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {savingPdf ? "Saving PDF…" : "Save PDF"}
                </button>
              </div>
              {pdfError ? <p role="alert" className="mt-3 text-xs text-red-400">{pdfError}</p> : null}

              <div className="mt-4 w-full min-w-0">
                <div
                  className={`mx-auto w-full min-w-0 max-w-[1120px] rounded-2xl border p-3 lg:w-[85%] ${
                    chartTheme === "light"
                      ? "bg-white border-slate-300"
                      : "bg-slate-900 border-slate-800"
                  }`}
                >
                  {!yCol ? (
                    !barPlot ? (
                      <div className="text-slate-400">No chart data.</div>
                    ) : (
                      <svg
                        ref={chartSvgRef}
                        width="100%"
                        height="auto"
                        viewBox={`0 0 ${barPlot.W} ${barPlot.H}`}
                        className="block h-auto w-full"
                      >
                        <rect
                          x="0"
                          y="0"
                          width={barPlot.W}
                          height={barPlot.H}
                          rx="16"
                          ry="16"
                          fill={chartTheme === "light" ? "#ffffff" : "#0f172a"}
                        />

                        <text
                          x={barPlot.pad.l + barPlot.innerW / 2}
                          y="28"
                          textAnchor="middle"
                          fontSize="16"
                          fontWeight="900"
                          fill={chartTheme === "light" ? "#0f172a" : "rgba(241,245,249,0.98)"}
                        >
                          {prettyHeader(xCol)}
                        </text>

                        {barPlot.ticks.map((tick) => (
                          <g key={`bartick-${tick.value}`}>
                            <line
                              x1={tick.x}
                              x2={tick.x}
                              y1={barPlot.pad.t - 4}
                              y2={barPlot.H - barPlot.pad.b + 4}
                              stroke={
                                Math.abs(tick.value) < 1e-10
                                  ? chartTheme === "light"
                                    ? "rgba(71,85,105,0.42)"
                                    : "rgba(148,163,184,0.32)"
                                  : chartTheme === "light"
                                    ? "rgba(148,163,184,0.14)"
                                    : "rgba(148,163,184,0.08)"
                              }
                              strokeWidth={Math.abs(tick.value) < 1e-10 ? "1.5" : "1"}
                            />
                            <text
                              x={tick.x}
                              y={barPlot.H - 20}
                              textAnchor="middle"
                              fontSize="11"
                              fontWeight="700"
                              fill={chartTheme === "light" ? "#475569" : "rgba(203,213,225,0.85)"}
                            >
                              {tick.label}
                            </text>
                          </g>
                        ))}

                        {barPlot.rows.map((p) => {
                          const seriesName =
                            series !== "All"
                              ? series
                              : String(
                                  p?.rawRow?.series ??
                                    p?.rawRow?.[col.series] ??
                                    "cup"
                                );
                          const img = p.useLogo && p.carNum != null ? numLogoSrc(p.carNum, seriesName) : "";
                          const valueOnRight = p.barWidth >= 0;
                          const valueTextX = valueOnRight
                            ? Math.min(barPlot.W - 10, p.valueX + 8)
                            : Math.max(barPlot.pad.l + 8, p.valueX - 8);

                          return (
                            <g key={`bar-${p.id}`}>
                              {p.useTeamLogo && p.teamName ? (
                                <g transform={`translate(28,${p.centerY})`}>
                                  <TeamLogoMark
                                    teamName={p.teamName}
                                    size={38}
                                    fallbackColor={chartTheme === "light" ? "#0f172a" : "rgba(226,232,240,0.95)"}
                                  />
                                </g>
                              ) : img ? (
                                <image
                                  href={img}
                                  x="8"
                                  y={p.centerY - 19}
                                  width="40"
                                  height="38"
                                  preserveAspectRatio="xMidYMid meet"
                                />
                              ) : null}

                              <text
                                x="58"
                                y={p.centerY + 4}
                                textAnchor="start"
                                fontSize="12"
                                fontWeight="800"
                                fill={chartTheme === "light" ? "#0f172a" : "rgba(241,245,249,0.96)"}
                              >
                                {p.driver}
                              </text>

                              <AutoColorBar
                                logoSrc={img}
                                x={p.zeroX}
                                y={p.barY}
                                width={p.barWidth}
                                height={p.barH}
                                fallbackColor={chartTheme === "light" ? "#0284c7" : "#38bdf8"}
                                chartTheme={chartTheme}
                              />

                              <text
                                x={valueTextX}
                                y={p.centerY + 4}
                                textAnchor={valueOnRight ? "start" : "end"}
                                fontSize="12"
                                fontWeight="900"
                                fill={chartTheme === "light" ? "#0f172a" : "rgba(241,245,249,0.98)"}
                              >
                                {p.valueLabel}
                              </text>

                              <title>
                                {p.driver}
                                {"\n"}
                                {prettyHeader(xCol)}: {p.xVal}
                              </title>
                            </g>
                          );
                        })}
                      </svg>
                    )
                  ) : !plot || !chartStats ? (
                    <div className="text-slate-400">No chart data.</div>
                  ) : (
                    <svg
                      ref={chartSvgRef}
                      width="100%"
                      height="auto"
                      viewBox={`0 0 ${plot.W} ${plot.H}`}
                      className="block h-auto w-full"
                    >
                      <rect
                        x="0"
                        y="0"
                        width={plot.W}
                        height={plot.H}
                        rx="16"
                        ry="16"
                        fill={chartTheme === "light" ? "#ffffff" : "#0f172a"}
                      />

                      {/* Numeric labels only — no gray chart lines. */}
                      {plot.xTicks.map((tick) => (
                        <g key={`xtick-${tick.value}`}>
                          <text
                            x={tick.x}
                            y={plot.pad.t + plot.innerH + 22}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="700"
                            fill={chartTheme === "light" ? "#334155" : "rgba(226,232,240,0.92)"}
                          >
                            {tick.label}
                          </text>
                        </g>
                      ))}

                      {plot.yTicks.map((tick) => (
                        <g key={`ytick-${tick.value}`}>
                          <text
                            x={plot.pad.l - 12}
                            y={tick.y + 4}
                            textAnchor="end"
                            fontSize="11"
                            fontWeight="700"
                            fill={chartTheme === "light" ? "#334155" : "rgba(226,232,240,0.92)"}
                          >
                            {tick.label}
                          </text>
                        </g>
                      ))}

                      {/* The only chart reference lines: X/Y averages. */}
                      <line
                        x1={plot.avgX}
                        x2={plot.avgX}
                        y1={plot.pad.t}
                        y2={plot.pad.t + plot.innerH}
                        stroke={chartTheme === "light" ? "rgba(2,132,199,0.70)" : "rgba(56,189,248,0.55)"}
                        strokeWidth="1.5"
                        strokeDasharray="6 6"
                      />
                      <line
                        x1={plot.pad.l}
                        x2={plot.pad.l + plot.innerW}
                        y1={plot.avgY}
                        y2={plot.avgY}
                        stroke={chartTheme === "light" ? "rgba(37,99,235,0.70)" : "rgba(59,130,246,0.55)"}
                        strokeWidth="1.5"
                        strokeDasharray="6 6"
                      />


                      <text
                        x={plot.pad.l + plot.innerW / 2}
                        y={plot.H - 18}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="800"
                        fill={chartTheme === "light" ? "#0f172a" : "rgba(241,245,249,0.98)"}
                      >
                        {prettyHeader(xCol)}
                      </text>
                      <text
                        x="18"
                        y={plot.pad.t + plot.innerH / 2}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="800"
                        fill={chartTheme === "light" ? "#0f172a" : "rgba(241,245,249,0.98)"}
                        transform={`rotate(-90 18 ${plot.pad.t + plot.innerH / 2})`}
                      >
                        {prettyHeader(yCol)}
                      </text>

                      {plot.points.map((p) => {
                        // When the user explicitly selects a series, always use
                        // that series for the car-number logo folder. This keeps
                        // Cup / O'Reilly / Trucks logos in sync with the filter.
                        // If Series = All, fall back to the row's own series.
                        const seriesName =
                          series !== "All"
                            ? series
                            : String(
                                p?.rawRow?.series ??
                                  p?.rawRow?.[col.series] ??
                                  "cup"
                              );
                        const img = numLogoSrc(p.carNum, seriesName);

                        return (
                          <g key={p.id} transform={`translate(${p.x},${p.y})`}>
                            {/* Team charts use the logo itself as the marker — no bubble. */}
                            {!p.useTeamLogo ? (
                              <circle
                                cx="0"
                                cy="0"
                                r="16"
                                fill={chartTheme === "light" ? "rgba(255,255,255,0.92)" : "rgba(2,6,23,0.35)"}
                                stroke={chartTheme === "light" ? "rgba(71,85,105,0.35)" : "rgba(148,163,184,0.25)"}
                              />
                            ) : null}

                            {p.useTeamLogo && p.teamName ? (
                              <TeamLogoMark
                                teamName={p.teamName}
                                size={64}
                                fallbackColor={chartTheme === "light" ? "#0f172a" : "rgba(226,232,240,0.95)"}
                              />
                            ) : p.useLogo && p.carNum != null ? (
                              <image
                                href={img}
                                x={-14}
                                y={-14}
                                width={28}
                                height={28}
                                preserveAspectRatio="xMidYMid meet"
                              />
                            ) : (
                              <text
                                x="0"
                                y="4"
                                textAnchor="middle"
                                fontSize="9"
                                fontWeight="700"
                                fill={chartTheme === "light" ? "#0f172a" : "rgba(226,232,240,0.95)"}
                              >
                                {String(p.driver || "?").slice(0, 2).toUpperCase()}
                              </text>
                            )}
                            <title>
                              {p.driver}
                              {"\n"}
                              {prettyHeader(xCol)}: {p.xVal}
                              {"\n"}
                              {prettyHeader(yCol)}: {p.yVal}
                            </title>
                          </g>
                        );
                      })}
                    </svg>
                  )}
                </div>
              </div>
            </div>

            <div className="text-[11px] text-slate-400">
              Tip: Aggregate By changes the grouping only; the selected race, track, year, and track-type filters still apply first.
            </div>
          </>
        )}

        {raceLogTarget ? (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-3 md:p-6"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setRaceLogTarget(null);
            }}
          >
            <div className="w-[min(98vw,110rem)] max-h-[92vh] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/90 px-4 py-3">
                <div>
                  <div className="text-lg font-bold text-slate-50">
                    {raceLogTarget.label} Race Log
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Chronological order: earliest → latest • {raceLogRows.length.toLocaleString()} races
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex rounded-md border border-slate-700 bg-slate-950 p-1">
                    <button
                      type="button"
                      onClick={() => setRaceLogScope("filtered")}
                      className={`rounded px-3 py-1.5 text-xs font-semibold ${
                        raceLogScope === "filtered"
                          ? "bg-sky-500 text-slate-950"
                          : "text-slate-300 hover:bg-slate-800"
                      }`}
                      title="Only races included by the current Aggregate controls"
                    >
                      Filtered Race Log
                    </button>
                    <button
                      type="button"
                      onClick={() => setRaceLogScope("full")}
                      className={`rounded px-3 py-1.5 text-xs font-semibold ${
                        raceLogScope === "full"
                          ? "bg-sky-500 text-slate-950"
                          : "text-slate-300 hover:bg-slate-800"
                      }`}
                      title="Entire history for this driver/car in the selected series"
                    >
                      Full Race Log
                    </button>
                  </div>

                  <button
                    type="button"
                    disabled={!raceLogRows.length}
                    onClick={() =>
                      exportToCsv(
                        `NASCAR_RaceLog_${String(raceLogTarget.label).replace(/[^a-z0-9]+/gi, "_")}_${raceLogScope}.csv`,
                        raceLogRows,
                        raceLogColumns
                      )
                    }
                    className="rounded-md border border-sky-400 bg-sky-500 px-3 py-2 text-xs font-semibold text-slate-950 hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Export Race Log CSV
                  </button>

                  <button
                    type="button"
                    onClick={() => setRaceLogTarget(null)}
                    className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-slate-800"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="max-h-[calc(92vh-76px)] overflow-auto">
                <table className="min-w-max w-full text-xs leading-tight">
                  <thead className="sticky top-0 z-20 bg-slate-900">
                    <tr>
                      {raceLogColumns.map((c) => (
                        <th key={c} className="whitespace-nowrap px-2 py-2 text-center font-semibold text-slate-200">
                          {prettyHeader(c)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!raceLogRows.length ? (
                      <tr>
                        <td colSpan={raceLogColumns.length} className="px-3 py-8 text-center text-slate-400">
                          No matching races.
                        </td>
                      </tr>
                    ) : (
                      raceLogRows.map((r, i) => (
                        <tr key={`${r.race_date}-${r.race}-${i}`} className={i % 2 ? "bg-slate-900" : "bg-slate-950"}>
                          {raceLogColumns.map((c) => (
                            <td key={c} className="whitespace-nowrap px-2 py-1.5 text-center text-slate-100">
                              {c === "race_date"
                                ? formatEasternRaceDate(r?.[c])
                                : c === "gfs" && r?.[c] != null && r?.[c] !== ""
                                ? `${fmt(r?.[c])} mph`
                                : c === "pct_laps_completed" && r?.[c] != null && r?.[c] !== ""
                                ? `${Number(r?.[c]).toFixed(1)}%`
                                : fmt(r?.[c])}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
